import React, { useState, useEffect, Fragment, useRef } from 'react';
import {
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Autocomplete,
  Tab,
  Tabs,
  Chip,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import SearchIcon from '@mui/icons-material/Search';
import KeyIcon from '@mui/icons-material/VpnKey';
import FolderIcon from '@mui/icons-material/Folder';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import awsLogo from '../assets/Website/AWS.svg';
import googleLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/Website/Microsoft_Azure.svg';
import cloudSvg from '../assets/icons-home/cloud.svg';
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import { AiApiKeyTypes, AiLlmTypes, ApiTypes, HttpError } from '@flashbacktech/flashbackclient/api';

import { useApi } from '../contexts/ApiContext';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { toastError, toastSuccess } from '../utils/toast';
import { StyledButton, StyledButtonIcon } from '../components/StyledButton';
import { Dialog as HeadlessUIDialog, Transition } from '@headlessui/react';
import CommonFields from '../components/storage/CommonFields';
import StorageTypeFields from '../components/storage/StorageTypeFields';
import { EncryptedKey } from '@flashbacktech/flashbackclient/dist/api/types/storage/storage';
import { TabIndex } from '../navigation';

// AI Icon component (same as sidebar)
const AIIcon = ({
  size = 20,
  color = '#617BFF',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`repo-tabpanel-${index}`}
      aria-labelledby={`repo-tab-${index}`}
      className="py-4"
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

// Style pour cacher les barres de défilement
const hideScrollbarStyle = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
};

// Interface pour les statistiques d'unité
interface UnitStats {
  calls: number;
  storageChange: number;
  storageChangeFormatted: string;
  downloadedBytes: number;
  downloadedBytesFormatted: string;
  uploadedBytes: number;
  uploadedBytesFormatted: string;
  isOnline: boolean;
  lastSeen: number;
}

function StorageRepoDetails() {
  const { apiClient } = useApi();
  const { isAuthenticated, authState } = useAuth();
  const navigate = useNavigate();
  const { repoId } = useParams<{ repoId: string }>();
  const location = useLocation();
  const withTokenRefresh = useWithTokenRefresh();
  const [repo, setRepo] = useState<ApiTypes.StorageRepoWithBuckets | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState<{ [key: string]: boolean }>({});
  const [editingApiKey, setEditingApiKey] = useState<{
    id: string;
    name: string;
    accessType: ApiTypes.AccessType;
    key: string;
    secret: string;
  } | null>(null);
  const apiKeysRef = useRef<HTMLDivElement>(null);
  const bucketsRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const [storageTypeAnchorEl, setStorageTypeAnchorEl] = useState<null | HTMLElement>(null);
  const isStorageTypeMenuOpen = Boolean(storageTypeAnchorEl);

  // Variables pour le menu d'édition
  const [selectedRepo, setSelectedRepo] = useState<ApiTypes.StorageRepoWithBuckets | null>(null);
  const [openEditRepo, setOpenEditRepo] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [availableUnits, setAvailableUnits] = useState<ApiTypes.StorageUnit[]>([]);
  const [bucketSearchQuery, setBucketSearchQuery] = useState('');
  const [apiKeySearchQuery, setApiKeySearchQuery] = useState('');
  const [apiKeyTypeFilter, setApiKeyTypeFilter] = useState<'all' | 's3' | 'ai'>('all');
  const [createApiKeyMenuAnchor, setCreateApiKeyMenuAnchor] = useState<null | HTMLElement>(null);
  const [apiKeysPage, setApiKeysPage] = useState(1);
  const API_KEYS_PER_PAGE = 5;
  
  // Menu anchors for Buckets section Create/Add buttons
  const [createResourceMenuAnchor, setCreateResourceMenuAnchor] = useState<null | HTMLElement>(null);
  const [addResourceMenuAnchor, setAddResourceMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Auto-attach modal state
  const [showAutoAttachModal, setShowAutoAttachModal] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<{
    repoId: string;
    repoName: string;
    resourceId?: string;
    resourceName?: string;
  } | null>(null);
  
  // Reset page when filter or search changes
  useEffect(() => {
    setApiKeysPage(1);
  }, [apiKeySearchQuery, apiKeyTypeFilter]);
  
  // Check for resource creation return context
  useEffect(() => {
    const checkReturnContext = () => {
      const returnContext = localStorage.getItem('returnToRepo');
      if (returnContext) {
        try {
          const context = JSON.parse(returnContext);
          // Check if context is recent (within 5 minutes)
          const isRecent = Date.now() - context.timestamp < 5 * 60 * 1000;
          
          if (isRecent && context.repoId === repo?.id) {
            // Check if there's a newly created resource flag
            const newResource = localStorage.getItem('newlyCreatedResource');
            if (newResource) {
              try {
                const resourceData = JSON.parse(newResource);
                setPendingAttachment({
                  repoId: context.repoId,
                  repoName: context.repoName,
                  resourceId: resourceData.id,
                  resourceName: resourceData.name
                });
                setShowAutoAttachModal(true);
                
                // Clean up the flags
                localStorage.removeItem('newlyCreatedResource');
              } catch (e) {
                console.error('Error parsing resource data:', e);
              }
            }
            
            // Clean up return context after processing
            localStorage.removeItem('returnToRepo');
          }
        } catch (e) {
          console.error('Error parsing return context:', e);
          localStorage.removeItem('returnToRepo');
        }
      }
    };
    
    // Check on mount and when repo changes
    if (repo?.id) {
      checkReturnContext();
    }
    
    // Also listen for custom event from StorageUnits page
    const handleResourceCreated = (event: any) => {
      const { resourceId, resourceName } = event.detail || {};
      if (resourceId && repo?.id) {
        const returnContext = localStorage.getItem('returnToRepo');
        if (returnContext) {
          try {
            const context = JSON.parse(returnContext);
            if (context.repoId === repo.id) {
              setPendingAttachment({
                repoId: context.repoId,
                repoName: context.repoName,
                resourceId,
                resourceName
              });
              setShowAutoAttachModal(true);
              localStorage.removeItem('returnToRepo');
            }
          } catch (e) {
            console.error('Error:', e);
          }
        }
      }
    };
    
    window.addEventListener('resourceCreated', handleResourceCreated);
    return () => {
      window.removeEventListener('resourceCreated', handleResourceCreated);
    };
  }, [repo?.id]);
  
  // AI LLM states
  const [openAddAILLMModal, setOpenAddAILLMModal] = useState(false);
  const [availableAILLMs, setAvailableAILLMs] = useState<AiLlmTypes.AiLlmDTO[]>([]);
  const [loadingAILLMs, setLoadingAILLMs] = useState(false);
  const [selectedAILLM, setSelectedAILLM] = useState<AiLlmTypes.AiLlmDTO | null>(null);
  const [linkingAILLM, setLinkingAILLM] = useState(false);
  const [showCreateNewAILLM, setShowCreateNewAILLM] = useState(false);
  const [newAILLM, setNewAILLM] = useState<AiLlmTypes.CreateAiLlmRequest>({
    name: '',
    aiType: AiLlmTypes.AiType.OPENAI,
    endpoint: '',
    key: '',
    secret: '',
    workspaceId: '',
  });
  const [creatingAILLM, setCreatingAILLM] = useState(false);
  
  // AI API Keys states
  const [openCreateAIApiKeyModal, setOpenCreateAIApiKeyModal] = useState(false);
  const [creatingAIApiKey, setCreatingAIApiKey] = useState(false);
  const [newAIApiKey, setNewAIApiKey] = useState<AiApiKeyTypes.CreateRepoAiApiKeyRequest>({
    name: '',
    aiType: AiLlmTypes.AiType.OPENAI,
  });
  const [createdAIKey, setCreatedAIKey] = useState<{ id: string; key: string; secret: string } | null>(null);
  const [openAISecretModal, setOpenAISecretModal] = useState(false);
  const [aiSecretCopied, setAISecretCopied] = useState(false);
  const [aiKeyCopied, setAIKeyCopied] = useState(false);
  
  // Variables pour la validation et la gestion des API keys
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [newApiKey, setNewApiKey] = useState<{
    name: string;
    accessType: ApiTypes.AccessType;
  }>({ name: '', accessType: ApiTypes.AccessType.READ });
  
  // S3 API Key modal states
  const [openCreateS3ApiKeyModal, setOpenCreateS3ApiKeyModal] = useState(false);
  const [creatingS3ApiKey, setCreatingS3ApiKey] = useState(false);
  const [newS3ApiKey, setNewS3ApiKey] = useState<{ name: string; accessType: ApiTypes.AccessType }>({
    name: '',
    accessType: ApiTypes.AccessType.READ,
  });
  const [createdS3Key, setCreatedS3Key] = useState<{ id: string; key: string; secret: string } | null>(null);
  const [openS3SecretModal, setOpenS3SecretModal] = useState(false);
  const [s3SecretCopied, setS3SecretCopied] = useState(false);
  const [s3KeyCopied, setS3KeyCopied] = useState(false);
  const [showS3ConfirmClose, setShowS3ConfirmClose] = useState(false);
  const [showAIConfirmClose, setShowAIConfirmClose] = useState(false);
  const [newRepo, setNewRepo] = useState<{
    name: string;
    storageType: ApiTypes.StorageType;
    mode: ApiTypes.ModeType;
    repoUnits: { unitId: string; folder: string; master: boolean }[];
    apiKeys: { name: string; accessType: ApiTypes.AccessType; key?: string }[];
  }>({
    name: '',
    storageType: ApiTypes.StorageType.S3,
    mode: ApiTypes.ModeType.NORMAL,
    repoUnits: [],
    apiKeys: [],
  });

  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();
  
  const getWorkspaceIdToUse = () => {
    if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') {
      throw new Error('Please select a workspace');
    }
    return selectedWorkspaceId;
  };

  // Helper to fetch repos - ALWAYS fetch all repos for details page (no workspace filter)
  const fetchReposWithWorkspace = React.useCallback(async (): Promise<{ repos: any[] }> => {
    try {
      // Always fetch ALL repos without workspace filter for details page
      const response = await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId));
      return { repos: response.repos || [] };
    } catch (e) {
      const r = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
      return { repos: r?.repos || [] } as any;
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // État pour la pagination des buckets (plus nécessaire avec le tableau)
  // const [currentBucketIndex, setCurrentBucketIndex] = useState(0);
  // const bucketsPerPage = 3; // Nombre de buckets à afficher par page

  // État pour stocker les statistiques réelles des unités
  const [unitStats, setUnitStats] = useState<Record<string, UnitStats>>({});
  
  // État pour stocker les statistiques des AI LLMs
  const [aiLlmStats, setAiLlmStats] = useState<Record<string, { calls: number; tokensIn: number; tokensOut: number }>>({});
  
  // Function to reload repo data
  const reloadRepoData = React.useCallback(async () => {
    if (!repoId) return;
    
    try {
      const reposResponse = await fetchReposWithWorkspace();
      if (reposResponse?.repos) {
        let foundRepo = reposResponse.repos.find((r) => r.id === repoId);
        
        if (!foundRepo) {
          const allResp = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
          foundRepo = allResp?.repos?.find((r: any) => r.id === repoId);
        }
        
        if (foundRepo) {
          setRepo(foundRepo);
        }
      }
    } catch (error) {
      console.error('Error reloading repo data:', error);
    }
  }, [repoId, fetchReposWithWorkspace, withTokenRefresh, apiClient]);

  // State for bucket edit dialog
  const [openEditUnit, setOpenEditUnit] = useState<boolean>(false);
  // State for add bucket dialog
  const [openAddBucket, setOpenAddBucket] = useState<boolean>(false);
  const [selectedBucketToAdd, setSelectedBucketToAdd] = useState<ApiTypes.StorageUnit | null>(null);
  const [bucketFolder, setBucketFolder] = useState<string>('');
  const [showCreateNewBucket, setShowCreateNewBucket] = useState<boolean>(false);
  const [creatingBucket, setCreatingBucket] = useState<boolean>(false);
  // State for repo name editing
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedRepoName, setEditedRepoName] = useState<string>('');
  // State for delete confirmation dialog
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [newUnit, setNewUnit] = useState<{
    name: string;
    bucket: string;
    storageType: ApiTypes.StorageType;
    status: string;
    unitId?: string;
  }>({ name: '', bucket: '', storageType: ApiTypes.StorageType.S3, status: 'UNKNOWN' });

  // Storage type specific fields
  const [s3Fields, setS3Fields] = useState<{
    bucket: string;
    accessKey: string;
    secret: string;
    endpoint: string;
    region: string;
  }>({ bucket: '', accessKey: '', secret: '', endpoint: '', region: '' });

  const [gcsFields, setGcsFields] = useState<{
    bucket: string;
    clientEmail: string;
    privateKey: string;
    endpoint: string;
  }>({ bucket: '', clientEmail: '', privateKey: '', endpoint: '' });

  const [azureFields, setAzureFields] = useState<{
    storageAccount: string;
    container: string;
    accessKey: string;
  }>({ storageAccount: '', container: '', accessKey: '' });

  // Utilisation des validationErrors déjà déclarées plus haut

  // Fonction pour formater les bytes en format lisible (KB, MB, GB, etc.)
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));

    // Gérer les valeurs négatives pour le changement de stockage
    const sign = bytes < 0 ? '-' : '';
    const formattedValue = parseFloat((Math.abs(bytes) / Math.pow(k, i)).toFixed(dm));

    return sign + formattedValue + ' ' + sizes[i];
  };

  // Fonction pour générer des statistiques avec des valeurs à zéro (utilisée comme fallback)
  const getRandomStats = (unitId?: string) => {
    return {
      calls: 0,
      storageChange: 0,
      storageChangeFormatted: '0 Bytes',
      downloadedBytes: 0,
      downloadedBytesFormatted: '0 Bytes',
      uploadedBytes: 0,
      uploadedBytesFormatted: '0 Bytes',
      isOnline: false,
      lastSeen: 0,
    };
  };

  // Reset form fields
  const resetForm = () => {
    setNewUnit({ name: '', bucket: '', storageType: ApiTypes.StorageType.S3, status: 'UNKNOWN' });
    setS3Fields({ bucket: '', accessKey: '', secret: '', endpoint: '', region: '' });
    setGcsFields({ bucket: '', clientEmail: '', privateKey: '', endpoint: '' });
    setAzureFields({ storageAccount: '', container: '', accessKey: '' });
    setValidationErrors({});
  };

  // Validate form fields
  const validateForm = (isEdit = false) => {
    const errors: Record<string, string> = {};

    // Validate common fields
    if (!newUnit.name.trim()) {
      errors.name = 'Bucket name is required';
    }

    // Validate storage type specific fields
    switch (newUnit.storageType) {
      case ApiTypes.StorageType.S3:
        if (!s3Fields.bucket.trim()) {
          errors.bucket = 'Bucket name is required';
        }
        if (!isEdit && !s3Fields.accessKey.trim()) {
          errors.accessKey = 'Access key is required';
        }
        if (!isEdit && !s3Fields.secret.trim()) {
          errors.secret = 'Secret key is required';
        }
        break;
      case ApiTypes.StorageType.GCS:
        if (!gcsFields.bucket.trim()) {
          errors.bucket = 'Bucket name is required';
        }
        if (!isEdit && !gcsFields.clientEmail.trim()) {
          errors.clientEmail = 'Client email is required';
        }
        if (!isEdit && !gcsFields.privateKey.trim()) {
          errors.privateKey = 'Private key is required';
        }
        break;
      case ApiTypes.StorageType.BLOB:
        if (!azureFields.container.trim()) {
          errors.container = 'Container name is required';
        }
        if (!isEdit && !azureFields.storageAccount.trim()) {
          errors.storageAccount = 'Storage account is required';
        }
        if (!isEdit && !azureFields.accessKey.trim()) {
          errors.accessKey = 'Access key is required';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper functions to get bucket, key, and secret based on storage type
  const getBucket = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
      case ApiTypes.StorageType.GCS:
        return storageType === ApiTypes.StorageType.S3 ? s3Fields.bucket : gcsFields.bucket;
      case ApiTypes.StorageType.BLOB:
        return azureFields.container;
    }
  };

  const getKey = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.accessKey;
      case ApiTypes.StorageType.GCS:
        return gcsFields.clientEmail;
      case ApiTypes.StorageType.BLOB:
        return azureFields.storageAccount;
    }
  };

  const getSecret = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3:
        return s3Fields.secret;
      case ApiTypes.StorageType.GCS:
        return gcsFields.privateKey;
      case ApiTypes.StorageType.BLOB:
        return azureFields.accessKey;
    }
  };

  // Fonction pour rafraîchir les données du repo
  const refreshRepoData = async () => {
    if (!repoId) return;

    try {
      setLoading(true);
      const reposResponse = await fetchReposWithWorkspace();

      if (reposResponse?.repos) {
        let foundRepo = reposResponse.repos.find((r) => r.id === repoId);
        // Fallback: if not found in filtered set, try without workspace filter
        if (!foundRepo) {
          const allResp = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
          foundRepo = allResp?.repos?.find((r: any) => r.id === repoId);
        }
        if (foundRepo) {
          setRepo(foundRepo);

          // Debug: Log AI API Keys data
          console.log('[StorageRepoDetails] Repo data:', foundRepo);
          console.log('[StorageRepoDetails] AI API Keys:', foundRepo.aiApiKeys);
          console.log('[StorageRepoDetails] AI API Keys length:', foundRepo.aiApiKeys?.length || 0);

          // Récupérer les statistiques pour chaque unité
          const unitIds = foundRepo.buckets
            .filter((bucket: any) => bucket.bucketId)
            .map((bucket: any) => bucket.bucketId as string);

          if (unitIds.length > 0) {
            fetchUnitStats(unitIds);
          }
        }
      }
    } catch (error) {
      toastError('Failed to refresh storage repo details');
    } finally {
      setLoading(false);
    }
  };

  // Handle create unit
  const handleCreateUnit = async () => {
    if (!validateForm()) {
      return;
    }

    const workspaceIdToUse = getWorkspaceIdToUse();

    try {
      const createStorageUnit: ApiTypes.CreateUnitRequest = {

        name: newUnit.name || '',
        bucket: getBucket(newUnit.storageType || ApiTypes.StorageType.S3),
        storageType: newUnit.storageType || ApiTypes.StorageType.S3,
        key: getKey(newUnit.storageType || ApiTypes.StorageType.S3),
        secret: getSecret(newUnit.storageType || ApiTypes.StorageType.S3),
        workspaceId: workspaceIdToUse,
      };

      // Add optional fields only if they are defined and not empty
      if (newUnit.storageType === ApiTypes.StorageType.S3) {
        if (s3Fields.region && s3Fields.region.trim() !== '') {
          (createStorageUnit as any).region = s3Fields.region;
        }
        if (s3Fields.endpoint && s3Fields.endpoint.trim() !== '') {
          (createStorageUnit as any).endpoint = s3Fields.endpoint;
        }
      } else if (newUnit.storageType === ApiTypes.StorageType.GCS) {
        if (gcsFields.endpoint && gcsFields.endpoint.trim() !== '') {
          (createStorageUnit as any).endpoint = gcsFields.endpoint;
        }
      }

      const response = await withTokenRefresh(() =>
        apiClient.createStorageBucket(createStorageUnit)
      );


      if (response.success) {
        // Add the new unit to the repo
        if (response.bucketId && repoId) {
          // Temporairement commenté jusqu'à ce que nous trouvions la méthode API correcte
          /* 
          // Utiliser l'API pour ajouter l'unité au repo
          await withTokenRefresh(() => apiClient.addUnitToRepo(repoId, createdUnitId));
          */

          // Pour l'instant, nous allons simplement afficher un message de succès

          // Save the ID of the new unit in sessionStorage
          const existingIds = sessionStorage.getItem('newlyCreatedUnits');
          const newUnitIds = existingIds ? JSON.parse(existingIds) : [];
          newUnitIds.push(response.bucketId);
          sessionStorage.setItem('newlyCreatedUnits', JSON.stringify(newUnitIds));

          // Refresh repo data
          refreshRepoData();
        }

        setOpenEditUnit(false);
        resetForm();
        toastSuccess('Storage unit created successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 400:
            toastError('Unit name must be unique');
            break;
          default:
            toastError(`Failed to create storage unit: ${error.message}`);
        }
      } else {
        toastError('Failed to create storage unit. Please try again.');
      }
    }
  };

  // Fonction pour récupérer les statistiques des unités via l'API
  // Fonction pour traiter les statistiques d'une unité
  const processUnitStat = (unitId: string, unitStat: any, stats: Record<string, any>) => {
    // Afficher les données brutes reçues pour déboguer

    // Format observé dans les logs:
    // {
    //   "unitId": "565eaa87-453b-4444-9f9e-9d6a7dcbcc83",
    //   "totalCount": "89",
    //   "totalUploadBytes": "1685270179",
    //   "totalDownloadBytes": "1576702928",
    //   "totalSizeChange": "-776787938"
    // }

    // Extraire les valeurs en convertissant les chaînes en nombres
    const requestCount = unitStat.totalCount
      ? parseInt(unitStat.totalCount, 10)
      : unitStat.requestCount || unitStat.calls || 0;

    const storageChangeBytes = unitStat.totalSizeChange
      ? parseInt(unitStat.totalSizeChange, 10)
      : unitStat.storageChangeBytes || unitStat.storageChange || unitStat.changes || 0;

    const downloadedBytes = unitStat.totalDownloadBytes
      ? parseInt(unitStat.totalDownloadBytes, 10)
      : unitStat.downloadedBytes || unitStat.downloaded || unitStat.down || 0;

    const uploadedBytes = unitStat.totalUploadBytes
      ? parseInt(unitStat.totalUploadBytes, 10)
      : unitStat.uploadedBytes || unitStat.uploaded || unitStat.up || 0;

    // Enregistrer les statistiques pour cette unité
    const statsObj = {
      calls: requestCount,
      storageChange: storageChangeBytes,
      storageChangeFormatted: formatBytes(storageChangeBytes),
      downloadedBytes: downloadedBytes,
      downloadedBytesFormatted: formatBytes(downloadedBytes),
      uploadedBytes: uploadedBytes,
      uploadedBytesFormatted: formatBytes(uploadedBytes),
      isOnline: unitStat.status === 'ONLINE',
      lastSeen: unitStat.lastSeenMinutes || 0,
    };

    stats[unitId] = statsObj;
  };

  const fetchUnitStats = async (unitIds: string[]) => {
    if (!unitIds.length) return;

    try {
      const newStats: Record<string, any> = {};

      console.log('[StorageRepoDetails] Fetching stats via V2 breakdown for:', unitIds);

      const breakdownResponse = await withTokenRefresh(() =>
        apiClient.getStorageGatewayBreakdown('buckets', { scope: 'org', window: '30d' } as any)
      ) as any;

      if (breakdownResponse?.entities && Array.isArray(breakdownResponse.entities)) {
        breakdownResponse.entities.forEach((entity: any) => {
          if (entity.entityId && unitIds.includes(entity.entityId)) {
            const totals = entity.totals || {};
            const statsObj = {
              calls: totals.requests || 0,
              storageChange: (totals.uplBytes || 0) - (totals.dwlBytes || 0),
              storageChangeFormatted: formatBytes((totals.uplBytes || 0) - (totals.dwlBytes || 0)),
              downloadedBytes: totals.dwlBytes || 0,
              downloadedBytesFormatted: formatBytes(totals.dwlBytes || 0),
              uploadedBytes: totals.uplBytes || 0,
              uploadedBytesFormatted: formatBytes(totals.uplBytes || 0),
              isOnline: true,
              lastSeen: 0,
            };
            newStats[entity.entityId] = statsObj;
          }
        });
      }

      unitIds.forEach((id) => {
        if (!newStats[id]) {
          console.warn('[StorageRepoDetails] No stats found for bucket', id, '- using fallback');
          newStats[id] = getRandomStats(id);
        }
      });

      console.log('[StorageRepoDetails] Final stats to set:', newStats);
      setUnitStats(newStats);
    } catch (error) {
      console.error('[StorageRepoDetails] Error fetching stats:', error);
      const newStats: Record<string, any> = {};
      unitIds.forEach((id) => {
        newStats[id] = getRandomStats(id);
      });
      setUnitStats(newStats);
    }
  };

  // Fetch AI LLM stats via V2 breakdown (single call for all models)
  const fetchAiLlmStats = async (aiLlmIds: string[]) => {
    if (!aiLlmIds.length) return;

    try {
      const newStats: Record<string, { calls: number; tokensIn: number; tokensOut: number }> = {};

      const response = await withTokenRefresh(() =>
        apiClient.getAiGatewayBreakdown('models', { scope: 'org', window: '30d' } as any)
      ) as any;

      if (response?.entities && Array.isArray(response.entities)) {
        const idSet = new Set(aiLlmIds);
        response.entities.forEach((entity: any) => {
          if (idSet.has(entity.entityId)) {
            newStats[entity.entityId] = {
              calls: entity.totals?.requests || 0,
              tokensIn: entity.totals?.tokensIn || 0,
              tokensOut: entity.totals?.tokensOut || 0,
            };
          }
        });
      }

      aiLlmIds.forEach((id) => {
        if (!newStats[id]) {
          newStats[id] = { calls: 0, tokensIn: 0, tokensOut: 0 };
        }
      });

      setAiLlmStats(newStats);
    } catch (error) {
      console.error('[StorageRepoDetails] Error fetching AI LLM stats:', error);
    }
  };

  // Fonction pour récupérer les unités de stockage disponibles
  const fetchAvailableUnits = async () => {
    try {
      const workspaceId = getWorkspaceIdToUse();
      const response = await withTokenRefresh(() => (apiClient as any).getAvailableStorageBuckets(workspaceId));
      // getAvailableStorageBuckets returns an array of buckets
      if (Array.isArray(response)) {
        setAvailableUnits(response as any);
      } else if (response && Array.isArray((response as any).buckets)) {
        setAvailableUnits((response as any).buckets as any);
      }
    } catch (error) { /* Ignore errors */ }
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isAuthenticated) {
        navigate('/');
        return;
      }

      if (!repoId) {
        navigate('/repositories/inventory');
        return;
      }

      // Récupérer les unités disponibles
      fetchAvailableUnits();

      try {
        setLoading(true);
        const reposResponse = await fetchReposWithWorkspace();

        if (isMounted) {
          if (reposResponse?.repos) {
            let foundRepo = reposResponse.repos.find((r) => r.id === repoId);

            // Fallback: if not found in filtered set, try without workspace filter
            if (!foundRepo) {
              const allResp = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
              foundRepo = allResp?.repos?.find((r: any) => r.id === repoId);
            }
            if (foundRepo) {
              setRepo(foundRepo);

              // Debug: Log AI API Keys data
              console.log('[StorageRepoDetails] Repo data:', foundRepo);
              console.log('[StorageRepoDetails] AI API Keys:', foundRepo.aiApiKeys);
              console.log('[StorageRepoDetails] AI API Keys length:', foundRepo.aiApiKeys?.length || 0);
              
              // Récupérer les vraies statistiques pour chaque unité
              const bucketIds = foundRepo.buckets
                .filter((bucket: any) => bucket.bucketId || bucket.unitId)
                .map((bucket: any) => (bucket.bucketId || bucket.unitId) as string);

              console.log('[StorageRepoDetails] Loading stats for buckets:', bucketIds);
              
              if (bucketIds.length > 0) {
                fetchUnitStats(bucketIds);
              } else {
                console.warn('[StorageRepoDetails] No bucket IDs found in repo.buckets:', foundRepo.buckets);
              }
              
              // Fetch AI LLM stats
              const aiLlmIds = (foundRepo.aiLlms || [])
                .filter((aiLlm: any) => aiLlm.aiLlm?.id || aiLlm.id || aiLlm.aiLlmId)
                .map((aiLlm: any) => (aiLlm.aiLlm?.id || aiLlm.id || aiLlm.aiLlmId) as string);
              
              if (aiLlmIds.length > 0) {
                fetchAiLlmStats(aiLlmIds);
              }
            } else {
              navigate('/repositories/inventory');
              toastError('Storage repo not found');
            }
          } else {
            navigate('/repositories/inventory');
            toastError('Failed to load storage repos');
          }
        }
      } catch (error) {
        if (isMounted) {
          toastError('Failed to load storage repo details');
          navigate('/repositories/inventory');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [
    apiClient,
    isAuthenticated,
    navigate,
    repoId,
    withTokenRefresh,
    fetchReposWithWorkspace,
    selectedWorkspaceId,
  ]);

  // If arriving with ?section=api-keys, scroll to API Keys section once repo is loaded
  useEffect(() => {
    if (!repo) return;
    const params = new URLSearchParams(location.search);
    if (params.get('section') === 'api-keys') {
      setTimeout(() => {
        apiKeysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [repo, location.search]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toastSuccess('Copied to clipboard');
  };

  // Fonction pour supprimer un repo
  const handleDeleteRepo = async (repoId: string) => {
    if (!window.confirm('Are you sure you want to delete this repository?')) {
      return;
    }

    try {
      const response = await withTokenRefresh(() => apiClient.deleteStorageRepo(repoId));

      if (response.success) {
        toastSuccess('Repository deleted successfully');
        navigate('/repositories/inventory');
      }
    } catch (error) {
      toastError('Failed to delete repository');
    }
  };

  const toggleSecretVisibility = (keyId: string) => {
    setShowSecret((prev) => ({
      ...prev,
      [keyId]: !prev[keyId],
    }));
  };

  const handleCreateApiKey = async () => {
    if (!repo) return;

    try {
      const createRepoKey: ApiTypes.CreateRepoKeyRequest = {
        repoId: repo.id,
        name: 'New API Key ' + new Date().toLocaleString(),
        accessType: ApiTypes.AccessType.READ,
      };

      const response = await withTokenRefresh(() => apiClient.createRepoKey(createRepoKey));

      if (response.success) {
        // Immediately offer a download of the key and secret (they won't be fully visible later)
        try {
          const fileName = `flashback-api-key-${repo.name || repo.id}-${new Date()
            .toISOString()
            .replace(/[:.]/g, '-')}.txt`;
          const content = [
            'Flashback API Credentials (save this file securely)',
            `Repository: ${repo.name} (${repo.id})`,
            `Created: ${new Date().toString()}`,
            '',
            `API Key: ${response.key}`,
            `Secret: ${response.secret || ''}`,
            '',
            'Important: This secret is shown only once. Keep it safe.',
          ].join('\n');
          const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch (error) { /* Ignore errors */ }

        // Update the repo state with the new API key
        setRepo((prevRepo) => {
          if (!prevRepo) return null;

          return {
            ...prevRepo,
            apiKeys: [
              ...(prevRepo.apiKeys || []),
              {
                id: response.id,
                name: 'New API Key',
                accessType: ApiTypes.AccessType.READ,
                key: response.key,
                secret: getEmptyEncryptedKey(response.secret || ''),
                createdAt: new Date().toISOString(),
              },
            ],
          };
        });

        // Set the new key for editing
        const newKey = {
          id: response.id,
          name: 'New API Key',
          accessType: ApiTypes.AccessType.READ,
          key: response.key,
          secret: response.secret || '',
        };
        setEditingApiKey(newKey);
        toastSuccess('API key created successfully');

        // Dispatch event to update quest completion in Sidebar
        window.dispatchEvent(new CustomEvent('repoApiKeyCreated'));
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('Storage repo not found');
            break;
          default:
            toastError(`Failed to create API key: ${error.message}`);
        }
      } else {
        toastError('Failed to create API key. Please try again.');
      }
    }
  };

  // Handle S3 API Key creation from modal
  const handleCreateS3ApiKey = async () => {
    if (!repo) return;
    
    setCreatingS3ApiKey(true);
    try {
      const createRepoKey: ApiTypes.CreateRepoKeyRequest = {
        repoId: repo.id,
        name: newS3ApiKey.name || 'New Storage API Key ' + new Date().toLocaleString(),
        accessType: newS3ApiKey.accessType,
      };

      const response = await withTokenRefresh(() => apiClient.createRepoKey(createRepoKey));

      if (response.success) {
        // Store the created key for display in secret modal
        setCreatedS3Key({
          id: response.id,
          key: response.key,
          secret: response.secret || '',
        });
        
        // Update repo state
        setRepo((prevRepo) => {
          if (!prevRepo) return null;
          return {
            ...prevRepo,
            apiKeys: [
              ...(prevRepo.apiKeys || []),
              {
                id: response.id,
                name: newS3ApiKey.name || 'New Storage API Key',
                accessType: newS3ApiKey.accessType,
                key: response.key,
                secret: getEmptyEncryptedKey(response.secret || ''),
                createdAt: new Date().toISOString(),
              },
            ],
          };
        });

        setOpenCreateS3ApiKeyModal(false);
        setOpenS3SecretModal(true);
        setNewS3ApiKey({ name: '', accessType: ApiTypes.AccessType.READ });
        
        // Dispatch event to update quest completion
        window.dispatchEvent(new CustomEvent('repoApiKeyCreated'));
      }
    } catch (error) {
      if (error instanceof HttpError) {
        toastError(`Failed to create API key: ${error.message}`);
      } else {
        toastError('Failed to create API key. Please try again.');
      }
    } finally {
      setCreatingS3ApiKey(false);
    }
  };

  const getEmptyEncryptedKey = (key: string): EncryptedKey => {
    return {
      key: key,
      iv: '',
      authTag: '',
      message: '',
    };
  };

  const handleUpdateApiKey = async (
    keyId: string,
    name: string,
    accessType: ApiTypes.AccessType
  ) => {
    if (!repo) return;

    try {
      const response = await withTokenRefresh(() =>
        apiClient.updateRepoKey(repo.id, keyId, { name, accessType })
      );

      if (response.success) {
        // Update the repo state with the updated API key
        setRepo((prevRepo) => {
          if (!prevRepo) return null;

          return {
            ...prevRepo,
            apiKeys:
              prevRepo.apiKeys?.map((key) =>
                key.id === keyId ? { ...key, name, accessType } : key
              ) || [],
          };
        });
        setEditingApiKey(null);
        toastSuccess('API key updated successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('API key not found');
            break;
          default:
            toastError(`Failed to update API key: ${error.message}`);
        }
      } else {
        toastError('Failed to update API key. Please try again.');
      }
    }
  };

  const handleDeleteApiKey = async (keyId: string) => {
    if (!repo) return;

    try {
      const response = await withTokenRefresh(() => apiClient.deleteRepoKey(repo.id, keyId));

      if (response.success) {
        // Update the repo state by removing the deleted API key
        setRepo((prevRepo) => {
          if (!prevRepo) return null;

          return {
            ...prevRepo,
            apiKeys: prevRepo.apiKeys?.filter((key) => key.id !== keyId) || [],
          };
        });
        toastSuccess('API key deleted successfully');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        switch (error.status) {
          case 404:
            toastError('API key not found');
            break;
          default:
            toastError(`Failed to delete API key: ${error.message}`);
        }
      } else {
        toastError('Failed to delete API key. Please try again.');
      }
    }
  };

  // AI LLM functions
  const loadAvailableAILLMs = async () => {
    setLoadingAILLMs(true);
    try {
      const response = await withTokenRefresh(() => apiClient.getAiLlms(repo?.workspaceId));
      if (response.success) {
        setAvailableAILLMs(response.aiLlms || []);
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error loading AI LLMs:', error);
      toastError(error.message || 'Failed to load AI LLMs');
    } finally {
      setLoadingAILLMs(false);
    }
  };

  const handleCreateAndLinkAILLM = async () => {
    if (!repo) {
      toastError('Repository not found');
      return;
    }

    if (!newAILLM.name.trim()) {
      toastError('AI LLM name is required');
      return;
    }

    if (!newAILLM.key?.trim()) {
      toastError('API Key is required');
      return;
    }

    setCreatingAILLM(true);
    try {
      // Prepare the workspace ID
      const workspaceIdToUse = repo.workspaceId || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Workspace ID is required');
      }

      // Create the AI LLM
      const createResponse = await withTokenRefresh(() => apiClient.createAiLlm(newAILLM));

      if (createResponse.success && createResponse.aiLlmId) {
        // Link it to the repository using updateStorageRepo
        const currentAiLlms = (repo.aiLlms || []).map((aiLlmAssoc: any) => ({
          aiLlmId: aiLlmAssoc.aiLlmId || aiLlmAssoc.aiLlm?.id,
        }));
        const newAiLlms = [
          ...currentAiLlms,
          {
            aiLlmId: createResponse.aiLlmId,
          },
        ];

        // Create update request (leave repoUnits undefined to not touch it)
        const updateRequest: ApiTypes.UpdateRepoRequest = {
          name: repo.name,
          storageType: repo.storageType,
          mode: repo.mode,
          workspaceId: workspaceIdToUse,
          repoAiLlms: newAiLlms,
        };

        const linkResponse = await withTokenRefresh(() =>
          apiClient.updateStorageRepo(repo.id, updateRequest)
        );

        if (linkResponse.success) {
          setOpenAddAILLMModal(false);
          setShowCreateNewAILLM(false);
          setNewAILLM({
            name: '',
            aiType: AiLlmTypes.AiType.OPENAI,
            endpoint: '',
            key: '',
            secret: '',
            workspaceId: '',
          });
          toastSuccess('AI LLM created and linked successfully');
          refreshRepoData();
        } else {
          toastError('AI LLM created but failed to link to repository');
        }
      } else {
        toastError('Failed to create AI LLM');
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error creating AI LLM:', error);
      if (error instanceof HttpError) {
        toastError(`Failed to create AI LLM: ${error.message}`);
      } else {
        toastError(error.message || 'Failed to create AI LLM');
      }
    } finally {
      setCreatingAILLM(false);
    }
  };

  const handleLinkExistingAILLM = async () => {
    if (!repo) {
      toastError('Repository not found');
      return;
    }

    if (!selectedAILLM) {
      toastError('Please select an AI LLM');
      return;
    }

    // Check if AI LLM is already linked
    const alreadyLinked = repo.aiLlms?.some(
      (aiLlmAssoc: any) => aiLlmAssoc.aiLlmId === selectedAILLM.id || aiLlmAssoc.aiLlm?.id === selectedAILLM.id
    );
    if (alreadyLinked) {
      toastError('This AI LLM is already linked to the repository');
      return;
    }

    setLinkingAILLM(true);
    try {
      // Prepare the workspace ID
      const workspaceIdToUse =
        (repo as any)?.workspaceId || (repo as any)?.workspace_id || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Workspace ID is required');
      }

      // Prepare the new AI LLM array with the new one added
      const currentAiLlms = (repo.aiLlms || []).map((aiLlmAssoc: any) => ({
        aiLlmId: aiLlmAssoc.aiLlmId || aiLlmAssoc.aiLlm?.id,
      }));
      const newAiLlms = [
        ...currentAiLlms,
        {
          aiLlmId: selectedAILLM.id,
        },
      ];

      // Create update request (leave repoUnits undefined to not touch it)
      const updateRequest: ApiTypes.UpdateRepoRequest = {
        name: repo.name,
        storageType: repo.storageType,
        mode: repo.mode,
        workspaceId: workspaceIdToUse,
        repoAiLlms: newAiLlms,
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageRepo(repo.id, updateRequest)
      );

      if (response.success) {
        setOpenAddAILLMModal(false);
        setSelectedAILLM(null);
        toastSuccess('AI LLM linked successfully');
        refreshRepoData();
      } else {
        toastError('Failed to link AI LLM');
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error linking AI LLM:', error);
      if (error instanceof HttpError) {
        toastError(`Failed to link AI LLM: ${error.message}`);
      } else {
        toastError(error.message || 'Failed to link AI LLM');
      }
    } finally {
      setLinkingAILLM(false);
    }
  };

  // Function to delete an AI API Key
  const handleDeleteAIApiKey = async (keyId: string) => {
    if (!repo) return;

    if (!window.confirm('Are you sure you want to delete this AI API key?')) {
      return;
    }

    try {
      const response = await withTokenRefresh(() => apiClient.deleteRepoAiApiKey(repo.id, keyId));
      if (response.success) {
        toastSuccess('AI API key deleted successfully');
        await refreshRepoData();
      } else {
        toastError('Failed to delete AI API key');
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error deleting AI API key:', error);
      if (error instanceof HttpError) {
        toastError(`Failed to delete AI API key: ${error.message}`);
      } else {
        toastError(error.message || 'Failed to delete AI API key');
      }
    }
  };

  const handleUnlinkAILLM = async (aiLlmId: string) => {
    if (!repo) return;

    if (!window.confirm('Are you sure you want to unlink this AI LLM from the repository?')) {
      return;
    }

    try {
      // Prepare the workspace ID
      const workspaceIdToUse =
        (repo as any)?.workspaceId || (repo as any)?.workspace_id || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Workspace ID is required');
      }

      // Filter out the AI LLM to unlink
      const updatedAiLlms = (repo.aiLlms || [])
        .filter((aiLlmAssoc: any) => {
          const assocId = aiLlmAssoc.aiLlmId || aiLlmAssoc.aiLlm?.id;
          return assocId !== aiLlmId;
        })
        .map((aiLlmAssoc: any) => ({
          aiLlmId: aiLlmAssoc.aiLlmId || aiLlmAssoc.aiLlm?.id,
        }));

      // Create update request (leave repoUnits undefined to not touch it)
      const updateRequest: ApiTypes.UpdateRepoRequest = {
        name: repo.name,
        storageType: repo.storageType,
        mode: repo.mode,
        workspaceId: workspaceIdToUse,
        repoAiLlms: updatedAiLlms,
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageRepo(repo.id, updateRequest)
      );

      if (response.success) {
        toastSuccess('AI LLM unlinked successfully');
        refreshRepoData();
      } else {
        toastError('Failed to unlink AI LLM');
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error unlinking AI LLM:', error);
      if (error instanceof HttpError) {
        toastError(`Failed to unlink AI LLM: ${error.message}`);
      } else {
        toastError(error.message || 'Failed to unlink AI LLM');
      }
    }
  };

  // AI API Keys functions
  const handleCreateAIApiKey = async () => {
    if (!repo) {
      toastError('Repository not found');
      return;
    }

    if (!newAIApiKey.name.trim()) {
      toastError('AI API Key name is required');
      return;
    }

    setCreatingAIApiKey(true);
    try {
      const response = await withTokenRefresh(() => apiClient.createRepoAiApiKey(repo.id, {
        name: newAIApiKey.name,
        aiType: newAIApiKey.aiType,
      }));

      if (response.success) {
        setCreatedAIKey({
          id: response.id,
          key: response.key,
          secret: response.secret,
        });
        setOpenCreateAIApiKeyModal(false);
        setOpenAISecretModal(true);
        setNewAIApiKey({ name: '', aiType: AiLlmTypes.AiType.OPENAI });
        
        // Reload repo to show new AI API key
        const reposResponse = await withTokenRefresh(() => apiClient.getStorageRepo(repo.id));

        if (reposResponse.success) {
          setRepo(reposResponse.repo);
          toastSuccess('AI API Key created successfully');
          window.dispatchEvent(new CustomEvent('repoApiKeyCreated'));
        } else {
          toastError('Failed to get repositories');
        }
      } else {
        toastError('Failed to create AI API Key');
      }
    } catch (error: any) {
      console.error('[StorageRepoDetails] Error creating AI API key:', error);
      toastError(error.message || 'Failed to create AI API Key');
    } finally {
      setCreatingAIApiKey(false);
    }
  };

  const copyAIKeyToClipboard = (text: string, type: 'key' | 'secret') => {
    navigator.clipboard.writeText(text);
    if (type === 'key') {
      setAIKeyCopied(true);
      setTimeout(() => setAIKeyCopied(false), 2000);
    } else {
      setAISecretCopied(true);
      setTimeout(() => setAISecretCopied(false), 2000);
    }
    toastSuccess('Copied to clipboard');
  };

  // Function to remove a bucket from the repo
  const handleRemoveBucketFromRepo = async (bucketId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (!repo || !bucketId) {
      return;
    }

    if (!window.confirm('Are you sure you want to remove this bucket from the repository?')) {
      return;
    }

    // Determine workspace to use: if global selection is ALL, use local selected; else use global
    const workspaceIdToUse = getWorkspaceIdToUse();

    try {
      // Get current buckets except the one to remove
      // Check all possible ID fields
      const updatedBuckets = repo.buckets.filter((b: any) => {
        const bId = b.bucketId || b.unitId || (b as any).id || b.bucket?.id;
        const shouldRemove = bId === bucketId;
        return !shouldRemove;
      });

      // Prepare the update request
      const workspaceIdToUse =
        (repo as any)?.workspaceId || (repo as any)?.workspace_id || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Workspace ID is required');
      }

      const updateRequest: ApiTypes.UpdateRepoRequest = {
        name: repo.name,
        storageType: repo.storageType,
        mode: repo.mode,
        workspaceId: workspaceIdToUse,
        repoUnits: updatedBuckets.map((bucket: any) => {
          const unitId =
            bucket.bucketId || bucket.unitId || (bucket as any).id || bucket.bucket?.id;
          return {
            unitId: unitId,
            folder: bucket.folder || '',
            master: bucket.master || false,
          };
        }),
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageRepo(repo.id, updateRequest)
      );

      if (response.success) {
        toastSuccess('Bucket removed from repository');
        // Refresh repo data
        refreshRepoData();
      }
    } catch (error) {
      if (error instanceof HttpError) {
        toastError(`Failed to remove bucket: ${error.message}`);
      } else {
        toastError('Failed to remove bucket. Please try again.');
      }
    }
  };

  // Function to add a bucket to the repo
  const handleAddBucketToRepo = async () => {
    if (!repo || !selectedBucketToAdd) return;

    try {
      // Check if bucket is already in the repo
      const alreadyExists = repo.buckets.some((b: any) => {
        const bId = b.bucketId || b.unitId || b.id || b.bucket?.id;
        return bId === selectedBucketToAdd.id;
      });
      if (alreadyExists) {
        toastError('This bucket is already in the repository');
        return;
      }

      // Prepare the new bucket to add
      const newBuckets = [
        ...repo.buckets.map((bucket: any) => ({
          unitId: bucket.bucketId || bucket.unitId || bucket.id || bucket.bucket?.id,
          folder: bucket.folder || '',
          master: bucket.master || false,
        })).filter((b: any) => b.unitId), // Filter out any with undefined unitId
        {
          unitId: selectedBucketToAdd.id,
          folder: bucketFolder || '',
          master: repo.buckets.length === 0, // Set as master if it's the first bucket
        },
      ];

      // Update the repo
      const workspaceIdToUse =
        (repo as any)?.workspaceId || (repo as any)?.workspace_id || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        throw new Error('Workspace ID is required');
      }

      const updateRequest: ApiTypes.UpdateRepoRequest = {
        name: repo.name,
        storageType: repo.storageType,
        mode: repo.mode,
        workspaceId: workspaceIdToUse,
        repoUnits: newBuckets,
      };

      const response = await withTokenRefresh(() =>
        apiClient.updateStorageRepo(repo.id, updateRequest)
      );

      if (response.success) {
        toastSuccess('Bucket added to repository');
        // Reset and close
        setSelectedBucketToAdd(null);
        setBucketFolder('');
        setOpenAddBucket(false);
        // Refresh repo data
        refreshRepoData();
      }
    } catch (error) {
      if (error instanceof HttpError) {
        toastError(`Failed to add bucket: ${error.message}`);
      } else {
        toastError('Failed to add bucket. Please try again.');
      }
    }
  };

  // Function to create a new bucket and link it to the repo
  const handleCreateAndLinkBucket = async () => {
    if (!repo || !newUnit.name) return;

    setCreatingBucket(true);
    try {
      const workspaceIdToUse = (repo as any)?.workspaceId || (repo as any)?.workspace_id || selectedWorkspaceId;
      if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
        toastError('Workspace ID is required');
        setCreatingBucket(false);
        return;
      }

      // Create the bucket
      const mapType = (t: ApiTypes.StorageType | undefined) =>
        (t === ApiTypes.StorageType.BLOB ? 'AZURE' : t) as any;

      const createBucketRequest: any = {
        name: newUnit.name || '',
        bucket: getBucket(newUnit.storageType || ApiTypes.StorageType.S3),
        storageType: mapType(newUnit.storageType || ApiTypes.StorageType.S3),
        key: getKey(newUnit.storageType || ApiTypes.StorageType.S3),
        secret: getSecret(newUnit.storageType || ApiTypes.StorageType.S3),
        workspaceId: workspaceIdToUse,
      };

      // Add optional fields for S3
      if (newUnit.storageType === ApiTypes.StorageType.S3) {
        if (s3Fields.region) createBucketRequest.region = s3Fields.region;
        if (s3Fields.endpoint) createBucketRequest.endpoint = s3Fields.endpoint;
      }

      const createResponse = await withTokenRefresh(() =>
        apiClient.createStorageBucket(createBucketRequest)
      );

      if (createResponse.success) {
        const newBucketId = (createResponse as any).bucketId || (createResponse as any).unitId;
        
        if (newBucketId) {
          // Link the bucket to the repo
          const newBuckets = [
            ...repo.buckets.map((bucket: any) => ({
              unitId: bucket.bucketId || bucket.unitId,
              folder: bucket.folder || '',
              master: bucket.master || false,
            })),
            {
              unitId: newBucketId,
              folder: '',
              master: repo.buckets.length === 0,
            },
          ];

          const updateRequest: ApiTypes.UpdateRepoRequest = {
            name: repo.name,
            storageType: repo.storageType,
            mode: repo.mode,
            workspaceId: workspaceIdToUse,
            repoUnits: newBuckets,
          };

          const linkResponse = await withTokenRefresh(() =>
            apiClient.updateStorageRepo(repo.id, updateRequest)
          );

          if (linkResponse.success) {
            toastSuccess('Bucket created and added to repository');
            // Reset form
            setNewUnit({ name: '', bucket: '', storageType: ApiTypes.StorageType.S3, status: 'UNKNOWN' });
            setS3Fields({ bucket: '', accessKey: '', secret: '', endpoint: '', region: '' });
            setGcsFields({ bucket: '', clientEmail: '', privateKey: '', endpoint: '' });
            setAzureFields({ storageAccount: '', container: '', accessKey: '' });
            setShowCreateNewBucket(false);
            setOpenAddBucket(false);
            // Refresh data
            refreshRepoData();
            // Dispatch event to refresh buckets list
            window.dispatchEvent(new Event('bucketsUpdated'));
          } else {
            toastError('Bucket created but failed to link to repository');
          }
        }
      } else {
        toastError('Failed to create bucket');
      }
    } catch (error) {
      if (error instanceof HttpError) {
        toastError(`Failed to create bucket: ${error.message}`);
      } else {
        toastError('Failed to create bucket. Please try again.');
      }
    } finally {
      setCreatingBucket(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full overflow-hidden">
        <Typography>Loading storage repo details...</Typography>
      </div>
    );
  }

  if (!repo) {
    return (
      <div className="flex justify-center items-center h-full overflow-hidden">
        <Typography>Storage repo not found</Typography>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" style={{ backgroundColor: '#FFFFFF', paddingLeft: '40px', paddingRight: '40px', paddingTop: '24px', paddingBottom: '24px' }}>
      {/* Page Header - aligned like StorageRepos */}
      <div style={{ marginBottom: '24px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Repository Details
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
            marginBottom: '16px',
          }}
        >
          View and manage repository configuration, resources and API keys
        </Typography>
        {/* Back button - between title and content */}
        <Button
          variant="text"
          startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
          onClick={() => navigate('/repositories/inventory')}
          sx={{ color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
        >
          Back to Repositories
        </Button>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Dialog for editing repo */}
          <Transition appear show={openEditRepo} as={Fragment}>
            <HeadlessUIDialog
              as="div"
              className="relative z-[100]"
              onClose={() => {
                setOpenEditRepo(false);
                setSelectedRepo(null);
              }}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/25" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <HeadlessUIDialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                      <HeadlessUIDialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900 mb-4"
                      >
                        Edit Repository
                      </HeadlessUIDialog.Title>
                      <div className="mt-2">
                        <Tabs
                          value={tabValue}
                          onChange={(_, newValue) => setTabValue(newValue)}
                          className="border-b border-gray-200 mb-4"
                          sx={{
                            '& .MuiTabs-indicator': {
                              backgroundColor: '#788CFF',
                              height: '3px',
                              borderRadius: '3px 3px 0 0',
                            },
                            '& .MuiTab-root': {
                              color: '#6b7280',
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              textTransform: 'none',
                              padding: '12px 24px',
                              '&.Mui-selected': {
                                color: '#788CFF',
                              },
                              '&:hover': {
                                color: '#6070DD',
                                backgroundColor: '#F0F2FF',
                              },
                            },
                          }}
                        >
                          <Tab label="General" id="repo-tab-0" aria-controls="repo-tabpanel-0" />
                          <Tab label="Buckets" id="repo-tab-1" aria-controls="repo-tabpanel-1" />
                          <Tab label="API Keys" id="repo-tab-2" aria-controls="repo-tabpanel-2" />
                        </Tabs>

                        <TabPanel value={tabValue} index={0}>
                          {/* Contenu de l'onglet General */}
                          <div className="space-y-4">
                            <div>
                              <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Repository Name
                              </label>
                              <input
                                type="text"
                                id="name"
                                value={newRepo.name}
                                onChange={(e) => setNewRepo({ ...newRepo, name: e.target.value })}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                          {/* Contenu de l'onglet Buckets */}
                          <div className="space-y-4">
                            {availableUnits.length === 0 ? (
                              <div className="text-gray-500 text-center py-4">
                                No buckets available. Please create a storage unit first.
                              </div>
                            ) : (
                              <>
                                <div className="flex flex-col space-y-4">
                                  {newRepo.repoUnits.map((unit, index) => (
                                    <div
                                      key={unit.unitId}
                                      className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg"
                                    >
                                      <div className="flex-1">
                                        <Typography variant="body1" className="font-medium">
                                          {availableUnits.find((u) => u.id === unit.unitId)?.name ||
                                            'Unknown Unit'}
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-500">
                                          {availableUnits.find((u) => u.id === unit.unitId)
                                            ?.bucket || 'Unknown Bucket'}
                                        </Typography>
                                      </div>
                                      <div className="flex items-center">
                                        <Tooltip title="Master Unit">
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              const updatedUnits = [...newRepo.repoUnits].map(
                                                (u) => ({
                                                  ...u,
                                                  master: u.unitId === unit.unitId,
                                                })
                                              );
                                              setNewRepo((prev) => ({
                                                ...prev,
                                                repoUnits: updatedUnits,
                                              }));
                                            }}
                                          >
                                            <div
                                              className={`w-4 h-4 rounded-full ${
                                                unit.master
                                                  ? 'bg-green-500 ring-2 ring-green-300'
                                                  : 'bg-gray-300'
                                              }`}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updatedUnits = newRepo.repoUnits.filter(
                                              (u) => u.unitId !== unit.unitId
                                            );
                                            // If we removed the master, set a new one if possible
                                            if (unit.master && updatedUnits.length > 0) {
                                              updatedUnits[0].master = true;
                                            }
                                            setNewRepo((prev) => ({
                                              ...prev,
                                              repoUnits: updatedUnits,
                                            }));
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" className="text-gray-500" />
                                        </IconButton>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div>
                                  <Autocomplete
                                    options={availableUnits.filter(
                                      (unit) =>
                                        !newRepo.repoUnits.some((ru) => ru.unitId === unit.id)
                                    )}
                                    getOptionLabel={(option) => `${option.name} (${option.bucket})`}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Add Storage Unit"
                                        size="small"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                              borderColor: '#e5e7eb',
                                            },
                                            '&:hover fieldset': {
                                              borderColor: '#788CFF',
                                            },
                                            '&.Mui-focused fieldset': {
                                              borderColor: '#788CFF',
                                            },
                                          },
                                        }}
                                      />
                                    )}
                                    onChange={(_, value) => {
                                      if (value) {
                                        const isMaster = newRepo.repoUnits.length === 0;
                                        // Utiliser le nom du bucket comme dossier par défaut
                                        const defaultFolder = value.bucket
                                          ? value.bucket.toLowerCase().replace(/[^a-z0-9]/g, '-')
                                          : 'default';
                                        setNewRepo((prev) => ({
                                          ...prev,
                                          repoUnits: [
                                            ...prev.repoUnits,
                                            {
                                              unitId: value.id,
                                              folder: defaultFolder,
                                              master: isMaster,
                                            },
                                          ],
                                        }));
                                      }
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </TabPanel>

                        <TabPanel value={tabValue} index={2}>
                          {/* Contenu de l'onglet API Keys */}
                          <div className="space-y-4">
                            <div className="flex flex-col space-y-4">
                              {newRepo.apiKeys && newRepo.apiKeys.length > 0 ? (
                                newRepo.apiKeys.map((key, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                                  >
                                    <div>
                                      <Typography variant="body1" className="font-medium">
                                        {key.name}
                                      </Typography>
                                      <Typography variant="body2" className="text-gray-500">
                                        {key.accessType === ApiTypes.AccessType.READ
                                          ? 'Read Only'
                                          : key.accessType === ApiTypes.AccessType.WRITE
                                          ? 'Write Only'
                                          : 'Read & Write'}
                                      </Typography>
                                    </div>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedKeys =
                                          newRepo.apiKeys?.filter((_, i) => i !== index) || [];
                                        setNewRepo((prev) => ({
                                          ...prev,
                                          apiKeys: updatedKeys,
                                        }));
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" className="text-gray-500" />
                                    </IconButton>
                                  </div>
                                ))
                              ) : (
                                <div
                                  className="py-4 flex items-center justify-center"
                                  style={{ minHeight: 200 }}
                                >
                                  <button
                                    onClick={handleCreateApiKey}
                                    style={{
                                      backgroundColor: '#617BFF',
                                      color: 'white',
                                      width: '200px',
                                      height: '36px',
                                      borderRadius: '8px',
                                      borderWidth: '2px',
                                      borderColor: '#617BFF',
                                      padding: '8px 12px',
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    Get your first API keys
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="space-y-3 border-t pt-4 mt-4">
                              <Typography variant="subtitle1" className="font-medium">
                                Add New API Key
                              </Typography>
                              <div>
                                <label
                                  htmlFor="apiKeyName"
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Key Name
                                </label>
                                <input
                                  type="text"
                                  id="apiKeyName"
                                  value={newApiKey.name}
                                  onChange={(e) => {
                                    setNewApiKey((prev) => ({ ...prev, name: e.target.value }));
                                    if (e.target.value.trim() === '') {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        apiKeyName: 'API key name is required',
                                      }));
                                    } else {
                                      setValidationErrors((prev) => {
                                        const newErrors = { ...prev };
                                        delete newErrors.apiKeyName;
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                                {validationErrors.apiKeyName && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {validationErrors.apiKeyName}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label
                                  htmlFor="accessType"
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Access Type
                                </label>
                                <select
                                  id="accessType"
                                  value={newApiKey.accessType}
                                  onChange={(e) =>
                                    setNewApiKey((prev) => ({
                                      ...prev,
                                      accessType: e.target.value as ApiTypes.AccessType,
                                    }))
                                  }
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                  {Object.values(ApiTypes.AccessType).map((type) => (
                                    <option key={type} value={type}>
                                      {type === ApiTypes.AccessType.READ
                                        ? 'Read Only'
                                        : type === ApiTypes.AccessType.WRITE
                                        ? 'Write Only'
                                        : type}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="flex justify-end mt-4">
                                <button
                                  type="button"
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  onClick={() => {
                                    if (!newApiKey.name.trim()) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        apiKeyName: 'API key name is required',
                                      }));
                                      return;
                                    }

                                    // Ajouter la nouvelle clé API
                                    setNewRepo((prev) => ({
                                      ...prev,
                                      apiKeys: [
                                        ...prev.apiKeys,
                                        {
                                          name: newApiKey.name,
                                          accessType: newApiKey.accessType,
                                        },
                                      ],
                                    }));

                                    // Réinitialiser le formulaire
                                    setNewApiKey({
                                      name: '',
                                      accessType: ApiTypes.AccessType.READ,
                                    });
                                    setValidationErrors((prev) => {
                                      const newErrors = { ...prev };
                                      delete newErrors.apiKeyName;
                                      return newErrors;
                                    });
                                  }}
                                >
                                  Add Key
                                </button>
                              </div>
                            </div>
                          </div>
                        </TabPanel>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          onClick={() => setOpenEditRepo(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          onClick={async () => {
                            try {
                              if (!selectedRepo) return;

                              // Validation du formulaire
                              if (!newRepo.name.trim()) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  name: 'Repository name is required',
                                }));
                                setTabValue(0); // Revenir à l'onglet General
                                return;
                              }

                              // Vérifier qu'au moins une unité est sélectionnée
                              if (newRepo.repoUnits.length === 0) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  units: 'At least one storage unit must be selected',
                                }));
                                setTabValue(1); // Aller à l'onglet Buckets
                                return;
                              }

                              // Vérifier qu'une unité master est définie
                              if (!newRepo.repoUnits.some((unit) => unit.master)) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  master: 'A master unit must be selected',
                                }));
                                setTabValue(1); // Aller à l'onglet Buckets
                                return;
                              }

                              // Préparer la requête de mise à jour
                              const workspaceIdToUse =
                                (selectedRepo as any)?.workspaceId ||
                                (selectedRepo as any)?.workspace_id ||
                                selectedWorkspaceId;
                              if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
                                throw new Error('Workspace ID is required');
                              }

                              const updateRequest = {
                                id: selectedRepo.id,
                                name: newRepo.name,
                                storageType: newRepo.storageType,
                                mode: newRepo.mode,
                                workspaceId: workspaceIdToUse,
                                repoUnits: newRepo.repoUnits,
                                apiKeys: newRepo.apiKeys,
                              };

                              // Appel API pour mettre à jour le repo
                              const response = await withTokenRefresh(() =>
                                apiClient.updateStorageRepo(selectedRepo.id, updateRequest)
                              );

                              if (response.success) {
                                toastSuccess('Repository updated successfully');
                                setOpenEditRepo(false);
                                refreshRepoData(); // Rafraîchir les données
                              } else {
                                toastError('Failed to update repository');
                              }
                            } catch (error) {
                              toastError('Failed to update repository');
                            }
                          }}
                        >
                          Save Changes
                        </button>
                      </div>
                    </HeadlessUIDialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </HeadlessUIDialog>
          </Transition>

          <div className="flex justify-between items-center mb-4">
            <div>
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <TextField
                    value={editedRepoName}
                    onChange={(e) => setEditedRepoName(e.target.value)}
                    size="small"
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 600,
                        fontSize: '20px',
                        '& fieldset': { borderColor: '#617BFF' },
                        '&:hover fieldset': { borderColor: '#617BFF' },
                        '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (editedRepoName.trim() && editedRepoName !== repo.name) {
                        try {
                          const workspaceIdToUse =
                            (repo as any)?.workspaceId ||
                            (repo as any)?.workspace_id ||
                            selectedWorkspaceId;
                          if (!workspaceIdToUse || workspaceIdToUse === 'ALL') {
                            throw new Error('Workspace ID is required');
                          }

                          const updateRequest: ApiTypes.UpdateRepoRequest = {
                            name: editedRepoName.trim(),
                            storageType: repo.storageType,
                            mode: repo.mode,
                            workspaceId: workspaceIdToUse,
                            repoUnits: repo.buckets.map((bucket: any) => {
                              const unitId = bucket.bucketId || bucket.unitId || (bucket as any).id;
                              return {
                                unitId: unitId,
                                folder: bucket.folder || '',
                                master: bucket.master || false,
                              };
                            }),
                          };
                          const response = await withTokenRefresh(() =>
                            apiClient.updateStorageRepo(repo.id, updateRequest)
                          );
                          if (response.success) {
                            toastSuccess('Repository name updated');
                            refreshRepoData();
                            setIsEditingName(false);
                          }
                        } catch (error) {
                          toastError('Failed to update repository name');
                        }
                      } else {
                        setIsEditingName(false);
                      }
                    }}
                    sx={{ color: '#10B981' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                    </svg>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsEditingName(false);
                      setEditedRepoName(repo.name);
                    }}
                    sx={{ color: '#EF4444' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                    </svg>
                  </IconButton>
                </div>
              ) : (
                <Typography
                  variant="h5"
                  className="text-black"
                  style={{
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '30px',
                  }}
                >
                  {repo.name}
                </Typography>
              )}

              {/* Trois boutons d'informations */}
              <div className="flex gap-2 mt-3 mb-3">
                {/* Vignette Supported SDK */}
                <Tooltip 
                  title="SDKs supported by this repository"
                  arrow
                  placement="top"
                >
                  <div
                    className="px-3 py-1 bg-white border border-gray-200 rounded flex items-center gap-1"
                    style={{
                      borderRadius: '6px',
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '18px',
                      color: '#414651',
                    }}
                  >
                    <span style={{ color: '#6B7280', marginRight: '4px' }}>Supported SDK:</span>
                    {(() => {
                      const sdks: string[] = [];
                      // Storage SDK from repo.storageType (S3, GCS, or Blob)
                      const storageType = repo.storageType?.toUpperCase();
                      if (storageType === 'S3') sdks.push('S3');
                      else if (storageType === 'GCS') sdks.push('GCS');
                      else if (storageType === 'AZURE' || storageType === 'BLOB') sdks.push('Blob');
                      // AI SDK: OpenAI only (if AI LLMs exist)
                      if ((repo.aiLlms?.length || 0) > 0) {
                        sdks.push('OpenAI');
                      }
                      return sdks.length > 0 ? sdks.join(', ') : 'None';
                    })()}
                  </div>
                </Tooltip>

                {/* Bouton Nombre de Buckets */}
                <div
                  className="px-3 py-1 bg-white border border-gray-200 rounded"
                  style={{
                    borderRadius: '6px',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#414651',
                    textAlign: 'center' as const,
                  }}
                >
                  {(repo.buckets?.length || 0) + (repo.aiLlms?.length || 0)} resource{((repo.buckets?.length || 0) + (repo.aiLlms?.length || 0)) !== 1 ? 's' : ''}
                </div>

                {/* Bouton Nombre d'API Keys */}
                <div
                  className="px-3 py-1 bg-white border border-gray-200 rounded"
                  style={{
                    borderRadius: '6px',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: '#414651',
                    textAlign: 'center' as const,
                  }}
                >
                  {(repo.apiKeys?.length || 0) + (repo.aiApiKeys?.length || 0)} API key{((repo.apiKeys?.length || 0) + (repo.aiApiKeys?.length || 0)) !== 1 ? 's' : ''}
                </div>
              </div>

              {/* ID du repo */}
              <div
                className="flex items-center mt-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ width: 'fit-content', maxWidth: '400px' }}
                onClick={() => {
                  navigator.clipboard.writeText(repoId || '');
                  toastSuccess('Repository ID copied to clipboard');
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    color: '#667085',
                    fontSize: '12px',
                    fontWeight: 400,
                    marginRight: '8px',
                  }}
                >
                  ID:
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: '#181D27',
                    fontSize: '12px',
                    fontWeight: 500,
                    fontFamily: 'monospace',
                    marginRight: '8px',
                  }}
                >
                  {repoId || 'N/A'}
                </Typography>
                <ContentCopyIcon
                  style={{
                    fontSize: '14px',
                    color: '#667085',
                  }}
                />
              </div>
              
              {/* Quick Navigation Bar - REMOVED */}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={refreshRepoData}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  borderWidth: '1px',
                  padding: '10px',
                  border: '1px solid #D1D5DB',
                }}
                title="Reload data"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
              <Tooltip title="Settings">
                <IconButton
                  onClick={() => {
                    setSelectedRepo(repo);
                    setNewRepo({
                      name: repo.name,
                      storageType: repo.storageType,
                      mode: repo.mode,
                      repoUnits: repo.buckets?.map((bucket: any) => ({
                        unitId: bucket.bucketId || bucket.unitId || bucket.id,
                        folder: bucket.folder || '',
                        master: bucket.master || false,
                      })) || [],
                      apiKeys: repo.apiKeys?.map((key: any) => ({
                        name: key.name || '',
                        accessType: key.accessType || 'READ',
                        key: key.key,
                      })) || [],
                    });
                    setOpenEditRepo(true);
                  }}
                  sx={{
                    color: '#667085',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    width: '40px',
                    height: '40px',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                      borderColor: '#9CA3AF',
                    },
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete repository">
                <IconButton
                  onClick={() => setOpenDeleteConfirm(true)}
                  sx={{
                    color: '#EF4444',
                    border: '1px solid #FEE2E2',
                    borderRadius: '8px',
                    width: '40px',
                    height: '40px',
                    '&:hover': {
                      backgroundColor: '#FEE2E2',
                      borderColor: '#EF4444',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Delete Confirmation Dialog */}
          <Transition appear show={openDeleteConfirm} as={Fragment}>
            <HeadlessUIDialog
              as="div"
              className="relative z-[100]"
              onClose={() => setOpenDeleteConfirm(false)}
            >
              <div className="fixed inset-0 bg-black bg-opacity-25 z-50" />
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all border-l-4 border-red-500">
                    <div className="flex items-center mb-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                        <DeleteIcon sx={{ color: '#EF4444', fontSize: 24 }} />
                      </div>
                      <div>
                        <HeadlessUIDialog.Title className="text-lg font-semibold text-gray-900">
                          Delete Repository
                        </HeadlessUIDialog.Title>
                      </div>
                    </div>
                    <div className="mt-3 mb-6">
                      <p className="text-sm text-gray-600">
                        Are you sure you want to delete the repository{' '}
                        <span className="font-semibold text-gray-900">{repo.name}</span>?
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        This action cannot be undone. All data associated with this repository will
                        be permanently removed.
                      </p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setOpenDeleteConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          handleDeleteRepo(repo.id);
                          setOpenDeleteConfirm(false);
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Delete Repository
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </HeadlessUIDialog>
          </Transition>

          <div className="border-b border-gray-200 mb-6"></div>

          {/* Espacement supplémentaire */}
          <div style={{ height: '60px' }}></div>

          {/* Buckets Section - Table Style */}
          <div ref={bucketsRef} className="relative flex justify-center">
            {/* Carte grise en arrière-plan */}
            <div
              className="bg-gray-100 rounded-xl border border-[#E9EAEB] absolute"
              style={{
                height: `${40 + ((repo?.buckets || []).filter((bucket) => {
                  if (!bucketSearchQuery) return true;
                  const searchLower = bucketSearchQuery.toLowerCase();
                  return (
                    (bucket.bucket?.name || '').toLowerCase().includes(searchLower) ||
                    (bucket.bucket?.bucket || '').toLowerCase().includes(searchLower) ||
                    (bucket.bucket?.storageType || '').toLowerCase().includes(searchLower)
                  );
                }).length) * 72 + 40 + 60 + 2}px`,
                width: '1080px',
                top: '-40px',
                zIndex: 0,
              }}
            >
              {/* Titre Buckets + Summary + Search Bar */}
              <div className="px-6 pt-1 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#181D27',
                      lineHeight: '20px',
                    }}
                  >
                    Resources
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      fontWeight: 500,
                    }}
                  >
                    {(() => {
                      const bucketCount = repo?.buckets?.length || 0;
                      const aiLlmCount = repo?.aiLlms?.length || 0;
                      
                      // Calculate total stats from unitStats (buckets) + aiLlmStats (AI LLMs)
                      let totalRequests = 0;
                      let totalUpload = 0;
                      let totalDownload = 0;
                      let totalTokensIn = 0;
                      let totalTokensOut = 0;
                      // Sum bucket stats
                      Object.values(unitStats).forEach((stats: any) => {
                        totalRequests += stats?.calls || 0;
                        totalUpload += stats?.uploadedBytes || 0;
                        totalDownload += stats?.downloadedBytes || 0;
                      });
                      // Sum AI LLM stats
                      Object.values(aiLlmStats).forEach((stats: any) => {
                        totalRequests += stats?.calls || 0;
                        totalTokensIn += stats?.tokensIn || 0;
                        totalTokensOut += stats?.tokensOut || 0;
                      });
                      
                      // Format bytes helper
                      const formatBytes = (bytes: number) => {
                        if (bytes === 0) return '0 B';
                        const k = 1024;
                        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
                      };
                      
                      // Format tokens helper
                      const formatTokens = (tokens: number) => {
                        if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
                        if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
                        return tokens.toLocaleString();
                      };
                      
                      const totalTokens = totalTokensIn + totalTokensOut;
                      const parts = [];
                      parts.push(`${bucketCount} Bucket${bucketCount !== 1 ? 's' : ''}`);
                      parts.push(`${aiLlmCount} AI`);
                      parts.push(`${totalRequests} Requests`);
                      parts.push(`${formatBytes(totalUpload + totalDownload)} Data Transit`);
                      parts.push(`${formatTokens(totalTokens)} Tokens`);
                      
                      return `(${parts.join(' • ')})`;
                    })()}
                  </span>
                </div>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={bucketSearchQuery}
                  onChange={(e) => setBucketSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        style={{
                          color: '#9CA3AF',
                          fontSize: '18px',
                          marginRight: '6px',
                        }}
                      />
                    ),
                  }}
                  sx={{
                    width: '240px',
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover fieldset': {
                        borderColor: '#D1D5DB',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#617BFF',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '6px 10px',
                      fontSize: '13px',
                      '&::placeholder': {
                        color: '#9CA3AF',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Carte blanche principale */}
            <div
              className="bg-white rounded-xl border border-[#E9EAEB] relative"
              style={{
                height: `${(() => {
                  // Combine buckets and AI LLMs into a single resources list
                  const allResources: Array<{type: 'bucket' | 'ai-llm', data: any}> = [
                    ...(repo?.buckets || []).map(bucket => ({ type: 'bucket' as const, data: bucket })),
                    ...(repo?.aiLlms || []).map(aiLlm => ({ type: 'ai-llm' as const, data: aiLlm }))
                  ];

                  const filteredResources = allResources.filter((resource) => {
                    if (!bucketSearchQuery) return true;
                    const searchLower = bucketSearchQuery.toLowerCase();
                    if (resource.type === 'bucket') {
                      const bucket = resource.data;
                      return (
                        (bucket.bucket?.name || '').toLowerCase().includes(searchLower) ||
                        (bucket.bucket?.bucket || '').toLowerCase().includes(searchLower) ||
                        (bucket.bucket?.storageType || '').toLowerCase().includes(searchLower)
                      );
                    } else {
                      const aiLlm = resource.data.aiLlm || resource.data;
                      return (
                        (aiLlm.name || '').toLowerCase().includes(searchLower) ||
                        (aiLlm.aiType || '').toLowerCase().includes(searchLower)
                      );
                    }
                  });

                  // If no resources, show empty state (200px) + header (40px) + bottom bar (60px) = 300px minimum
                  if (filteredResources.length === 0) {
                    return 300;
                  }

                  // Calculate height: header (40px) + resources (72px each) + bottom bar (60px)
                  return filteredResources.length * 72 + 40 + 60;
                })()}px`,
                width: '1080px',
                zIndex: 1,
              }}
            >
              {/* Header Row - 40px height */}
              {(() => {
                const hasStorage = (repo?.buckets?.length || 0) > 0;
                const hasAI = (repo?.aiLlms?.length || 0) > 0;
                return (
                  <div
                    style={{
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      borderBottom: '1px solid #e5e7eb',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                    }}
                  >
                    <div style={{ width: hasStorage && hasAI ? '300px' : '400px', paddingLeft: '16px' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Name
                      </Typography>
                    </div>
                    {hasStorage && hasAI && (
                      <div style={{ width: '100px', textAlign: 'left', paddingLeft: '16px' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Type
                        </Typography>
                      </div>
                    )}
                    <div style={{ width: '120px', textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Requests
                      </Typography>
                    </div>
                    <div style={{ width: '120px', textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Upload
                      </Typography>
                    </div>
                    <div style={{ width: '120px', textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Download
                      </Typography>
                    </div>
                    <div style={{ width: '120px', textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Change
                      </Typography>
                    </div>
                    <div style={{ width: '120px', textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Tokens
                      </Typography>
                    </div>
                    <div style={{ flex: 1, textAlign: 'right', paddingRight: '24px' }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: '#6B7280',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textTransform: 'none',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Actions
                      </Typography>
                    </div>
                  </div>
                );
              })()}

              {/* Resources List Container - Scrollable area (Buckets + AI LLMs) */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                }}
              >
                {(() => {
                  // Combine buckets and AI LLMs into a single resources list
                  const allResources: Array<{type: 'bucket' | 'ai-llm', data: any}> = [
                    ...repo.buckets.map(bucket => ({ type: 'bucket' as const, data: bucket })),
                    ...(repo.aiLlms || []).map(aiLlm => ({ type: 'ai-llm' as const, data: aiLlm }))
                  ];

                  const filteredResources = allResources.filter((resource) => {
                    if (!bucketSearchQuery) return true;
                    const searchLower = bucketSearchQuery.toLowerCase();
                    if (resource.type === 'bucket') {
                      const bucket = resource.data;
                      return (
                        (bucket.bucket?.name || '').toLowerCase().includes(searchLower) ||
                        (bucket.bucket?.bucket || '').toLowerCase().includes(searchLower) ||
                        (bucket.bucket?.storageType || '').toLowerCase().includes(searchLower)
                      );
                    } else {
                      const aiLlm = resource.data.aiLlm || resource.data;
                      return (
                        (aiLlm.name || '').toLowerCase().includes(searchLower) ||
                        (aiLlm.aiType || '').toLowerCase().includes(searchLower)
                      );
                    }
                  });

                  if (allResources.length === 0) {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '200px',
                          gap: '16px',
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No resources connected yet
                        </Typography>
                        <button
                          onClick={(e) => setCreateResourceMenuAnchor(e.currentTarget)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 20px',
                            backgroundColor: '#617BFF',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#5A6FE8';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#617BFF';
                          }}
                        >
                          Connect your first resource
                        </button>
                      </div>
                    );
                  }

                  if (filteredResources.length === 0) {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '200px',
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No resources found matching "{bucketSearchQuery}"
                        </Typography>
                      </div>
                    );
                  }

                  return filteredResources.map((resource, index) => {
                    // Handle both bucket and AI LLM resources
                    if (resource.type === 'bucket') {
                      const bucket = resource.data;
                    // Debug: log bucket structure
                    if (index === 0) { /* Empty */ }

                    // Extract bucket ID from various possible fields
                    const bucketId =
                      bucket.bucketId ||
                      (bucket as any).unitId ||
                      (bucket as any).id ||
                      bucket.bucket?.id;

                    // Fonction pour obtenir le logo du storage type
                    const getStorageLogo = (storageType: string) => {
                      switch (storageType?.toUpperCase()) {
                        case 'S3':
                          return awsLogo;
                        case 'GCS':
                          return googleLogo;
                        case 'BLOB':
                          return azureLogo;
                        default:
                          return null;
                      }
                    };

                    const logo = getStorageLogo(bucket.bucket?.storageType || 'S3');

                    return (
                      <div
                        key={bucketId || index}
                        style={{
                          height: '72px',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#FFFFFF',
                          borderBottom: '1px solid #F0F0F0',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                        }}
                        onClick={(e) => {
                          // Don't navigate if clicking on the delete button
                          const target = e.target as HTMLElement;
                          if (target.closest('.delete-bucket-action')) {
                            return;
                          }
                          if (bucketId) {
                            // Dispatch event to navigate to Storage Units tab
                            try {
                              const rootEl = document.getElementById('root');
                              if (rootEl) {
                                rootEl.dispatchEvent(
                                  new CustomEvent('navigateToBuckets', {
                                    detail: { tabIndex: TabIndex.StorageUnits },
                                  })
                                );
                              }
                              window.dispatchEvent(
                                new CustomEvent('navigateToBuckets', {
                                  detail: { tabIndex: TabIndex.StorageUnits },
                                })
                              );
                              
                              // Wait a bit for tab change to complete before navigating
                              setTimeout(() => {
                                navigate(`/cloud-storage/buckets/${bucketId}`);
                              }, 100);
                            } catch (error) {
                              /* Ignore */
                              navigate(`/cloud-storage/buckets/${bucketId}`);
                            }
                          }
                        }}
                      >
                        {/* Name + Logo Section - width depends on resource types */}
                        <div
                          style={{
                            width: (repo?.buckets?.length || 0) > 0 && (repo?.aiLlms?.length || 0) > 0 ? '300px' : '400px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: '16px',
                          }}
                        >
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px',
                              flexShrink: 0,
                            }}
                          >
                            {logo ? (
                              <img
                                src={logo}
                                alt={bucket.bucket?.storageType || 'Storage'}
                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  backgroundColor: '#788CFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  style={{
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                  }}
                                >
                                  {(bucket.bucket?.name || 'B').charAt(0).toUpperCase()}
                                </Typography>
                              </div>
                            )}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Typography
                                variant="body2"
                                style={{
                                  color: '#181D27',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                }}
                              >
                                {bucket.bucket?.name || 'Unnamed Bucket'}
                              </Typography>
                              <Tooltip title="Copy bucket name">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(bucket.bucket?.name || '');
                                    toastSuccess('Bucket name copied');
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14, color: '#667085' }} />
                                </IconButton>
                              </Tooltip>
                            </div>
                            <Typography
                              variant="caption"
                              style={{
                                color: '#667085',
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '16px',
                              }}
                            >
                              {bucket.bucket?.bucket || 'Default'} •{' '}
                              {bucket.bucket?.storageType || 'S3'}
                            </Typography>
                          </div>
                        </div>

                        {/* Type Section - 100px (only if both storage and AI exist) */}
                        {(repo?.aiLlms?.length || 0) > 0 && (
                          <div
                            style={{
                              width: '100px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              paddingLeft: '16px',
                            }}
                          >
                            <Chip
                              label="Storage"
                              size="small"
                              sx={{
                                backgroundColor: '#EFF6FF',
                                color: '#1E40AF',
                                fontWeight: 600,
                                fontSize: '12px',
                              }}
                            />
                          </div>
                        )}

                        {/* Requests Section - 120px */}
                        <div
                          style={{
                            width: '120px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#667085',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            {bucketId && bucketId in unitStats
                              ? unitStats[bucketId].calls.toLocaleString()
                              : '0'}
                          </Typography>
                        </div>

                        {/* Upload Section - 120px (always shown for storage) */}
                        <div
                          style={{
                            width: '120px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#667085',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            {bucketId && bucketId in unitStats
                              ? unitStats[bucketId].uploadedBytesFormatted || '0 B'
                              : ''}
                          </Typography>
                        </div>

                        {/* Download Section - 120px (always shown for storage) */}
                        <div
                          style={{
                            width: '120px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#667085',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            {bucketId && bucketId in unitStats
                              ? unitStats[bucketId].downloadedBytesFormatted || '0 B'
                              : ''}
                          </Typography>
                        </div>

                        {/* Change Section - 120px (storage change) */}
                        <div
                          style={{
                            width: '120px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#667085',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            {bucketId && bucketId in unitStats
                              ? unitStats[bucketId].storageChangeFormatted || '0 B'
                              : ''}
                          </Typography>
                        </div>

                        {/* Tokens Section - 120px (N/A for buckets) */}
                        <div
                          style={{
                            width: '120px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              color: '#9CA3AF',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '20px',
                            }}
                          >
                            N/A
                          </Typography>
                        </div>

                        {/* Actions Section - pushed to right */}
                        <div
                          style={{
                            flex: 1,
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            paddingRight: '24px',
                            gap: '4px',
                          }}
                        >
                          {/* Edit button */}
                          <Tooltip title="Edit bucket">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (bucketId) {
                                  // Navigate to bucket details for editing
                                  navigate(`/buckets/${bucketId}`);
                                }
                              }}
                              sx={{ 
                                color: '#667085',
                                '&:hover': { 
                                  backgroundColor: '#F3F4F6',
                                  color: '#374151',
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          {/* Delete button */}
                          <Tooltip title="Remove bucket from repository">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (bucketId) {
                                  handleRemoveBucketFromRepo(bucketId, e);
                                }
                              }}
                              disabled={!bucketId}
                              sx={{ 
                                color: '#667085',
                                '&:hover': { 
                                  backgroundColor: '#FEE2E2',
                                  color: '#EF4444',
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    );
                    } else if (resource.type === 'ai-llm') {
                      // Handle AI LLM resources
                      const aiLlmData = resource.data.aiLlm || resource.data;
                      const aiLlmId = aiLlmData.id || resource.data.aiLlmId;
                      
                      // Get real AI LLM stats (fallback to 0 if not available)
                      const aiStats = aiLlmId && aiLlmStats[aiLlmId] 
                        ? aiLlmStats[aiLlmId] 
                        : { calls: 0, tokensIn: 0, tokensOut: 0 };
                      const totalTokens = aiStats.tokensIn + aiStats.tokensOut;

                      return (
                        <div
                          key={aiLlmId || `ai-llm-${index}`}
                          style={{
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#FFFFFF',
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'default',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                          }}
                        >
                          {/* Name Section - width depends on resource types */}
                          <div
                            style={{
                              width: (repo?.buckets?.length || 0) > 0 && (repo?.aiLlms?.length || 0) > 0 ? '300px' : '400px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              paddingLeft: '16px',
                            }}
                          >
                            {/* Provider Icon - Using correct logos */}
                            {(() => {
                              const provider = (aiLlmData.aiType || aiLlmData.provider || '').toUpperCase();
                              if (provider === 'OPENAI') {
                                return (
                                  <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
                                    <svg viewBox="0 0 24 24" style={{ width: '22px', height: '22px' }} fill="#10A37F">
                                      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4046-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                                    </svg>
                                  </div>
                                );
                              } else if (provider === 'ANTHROPIC') {
                                return (
                                  <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
                                    <img src={anthropicLogo} alt="Anthropic" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                                  </div>
                                );
                              } else if (provider === 'GOOGLE' || provider === 'GEMINI') {
                                return (
                                  <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
                                    <img src={googleCloudLogo} alt="Google" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                                  </div>
                                );
                              } else if (provider === 'AWS' || provider === 'BEDROCK') {
                                return (
                                  <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
                                    <img src={awsLogo} alt="AWS" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                                  </div>
                                );
                              } else {
                                return (
                                  <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, flexShrink: 0 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                    </svg>
                                  </div>
                                );
                              }
                            })()}
                            <div>
                              <Typography
                                variant="body2"
                                style={{
                                  color: '#181D27',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                }}
                              >
                                {aiLlmData.name || 'Unnamed AI LLM'}
                              </Typography>
                              <Typography
                                variant="caption"
                                style={{
                                  color: '#667085',
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  lineHeight: '16px',
                                }}
                              >
                                {aiLlmData.aiType || 'AI'}
                              </Typography>
                            </div>
                          </div>

                          {/* Type Section - 100px (only if both storage and AI exist) */}
                          {(repo?.buckets?.length || 0) > 0 && (
                            <div
                              style={{
                                width: '100px',
                                height: '72px',
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: '16px',
                              }}
                            >
                              <Chip
                                label="AI"
                                size="small"
                                sx={{
                                  backgroundColor: '#FEF3C7',
                                  color: '#92400E',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </div>
                          )}

                          {/* Requests Section - 120px (for AI: show requests) */}
                          <div
                            style={{
                              width: '120px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: '#667085',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                              }}
                            >
                              {aiStats.calls.toLocaleString()}
                            </Typography>
                          </div>

                          {/* Upload Section - 120px (N/A for AI LLMs) */}
                          <div
                            style={{
                              width: '120px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                              }}
                            >
                              N/A
                            </Typography>
                          </div>

                          {/* Download Section - 120px (N/A for AI LLMs) */}
                          <div
                            style={{
                              width: '120px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                              }}
                            >
                              N/A
                            </Typography>
                          </div>

                          {/* Change Section - 120px (N/A for AI LLMs) */}
                          <div
                            style={{
                              width: '120px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: '#9CA3AF',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                              }}
                            >
                              N/A
                            </Typography>
                          </div>

                          {/* Tokens Section - 120px (for AI: show total tokens) */}
                          <div
                            style={{
                              width: '120px',
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{
                                color: '#667085',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '20px',
                              }}
                            >
                              {totalTokens >= 1000000 ? `${(totalTokens / 1000000).toFixed(1)}M` : totalTokens >= 1000 ? `${(totalTokens / 1000).toFixed(1)}K` : totalTokens.toLocaleString()}
                            </Typography>
                          </div>

                          {/* Delete Action Section - pushed to right */}
                          <div
                            className="delete-resource-action"
                            style={{
                              flex: 1,
                              height: '72px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              paddingRight: '16px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (aiLlmId) {
                                handleUnlinkAILLM(aiLlmId);
                              }
                            }}
                          >
                            <Tooltip title="Remove AI LLM from repository">
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '4px',
                                  cursor: aiLlmId ? 'pointer' : 'not-allowed',
                                  color: aiLlmId ? '#EF4444' : '#D1D5DB',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  if (aiLlmId) {
                                    e.currentTarget.style.backgroundColor = '#FEE2E2';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  });
                })()}
              </div>

              {/* Bottom Action Bar - 60px height */}
              <div
                style={{
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  borderTop: '1px solid #F0F0F0',
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                  backgroundColor: 'white',
                }}
              >
                <div className="flex gap-3">
                  {/* Add Button - Violet bg, white text */}
                  <button
                    style={{
                      backgroundColor: '#617BFF',
                      color: 'white',
                      width: '100px',
                      height: '36px',
                      borderRadius: '8px',
                      border: '2px solid #617BFF',
                      padding: '8px 12px',
                      fontWeight: 500,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                    }}
                    onClick={(e) => setCreateResourceMenuAnchor(e.currentTarget)}
                  >
                    Add
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <Menu
                    anchorEl={createResourceMenuAnchor}
                    open={Boolean(createResourceMenuAnchor)}
                    onClose={() => setCreateResourceMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: '180px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setCreateResourceMenuAnchor(null);
                        // Load available buckets and open add bucket modal
                        fetchAvailableUnits();
                        setOpenAddBucket(true);
                      }}
                      sx={{
                        fontSize: '13px',
                        padding: '10px 16px',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={cloudSvg} alt="Bucket" style={{ width: '20px', height: '20px' }} />
                        <span>Bucket</span>
                      </div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCreateResourceMenuAnchor(null);
                        loadAvailableAILLMs();
                        setOpenAddAILLMModal(true);
                      }}
                      sx={{
                        fontSize: '13px',
                        padding: '10px 16px',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AIIcon size={20} color="#617BFF" />
                        <span>AI LLM</span>
                      </div>
                    </MenuItem>
                  </Menu>
                </div>
                <div style={{ color: '#667085', fontSize: '13px', fontWeight: 500 }}>
                  {(() => {
                    const allResources = [
                      ...repo.buckets,
                      ...(repo.aiLlms || [])
                    ];
                    const filteredResources = allResources.filter((resource: any) => {
                      if (!bucketSearchQuery) return true;
                      const searchLower = bucketSearchQuery.toLowerCase();
                      if ('bucket' in resource) {
                        return (
                          (resource.bucket?.name || '').toLowerCase().includes(searchLower) ||
                          (resource.bucket?.bucket || '').toLowerCase().includes(searchLower) ||
                          (resource.bucket?.storageType || '').toLowerCase().includes(searchLower)
                        );
                      } else {
                        const aiLlm = resource.aiLlm || resource;
                        return (
                          (aiLlm.name || '').toLowerCase().includes(searchLower) ||
                          (aiLlm.aiType || '').toLowerCase().includes(searchLower)
                        );
                      }
                    }).length;
                    const totalCount = allResources.length;
                    
                    if (bucketSearchQuery && filteredResources !== totalCount) {
                      return `${filteredResources} of ${totalCount} resource${totalCount !== 1 ? 's' : ''}`;
                    }
                    return `${totalCount} resource${totalCount !== 1 ? 's' : ''}`;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* API Keys Section */}
          <div style={{ height: '80px' }}></div>
          <div
            ref={apiKeysRef}
            id="api-keys-section"
            className="relative mt-0 mb-8 flex justify-center"
          >
            {/* Carte grise en arrière-plan */}
            <div
              className="bg-gray-100 rounded-xl border border-[#E9EAEB] absolute"
              style={{
                height: (() => {
                  const totalKeys = (repo?.apiKeys?.length || 0) + (repo?.aiApiKeys?.length || 0);
                  if (totalKeys === 0) return `${244 + 72}px`;
                  const totalPages = Math.ceil(totalKeys / API_KEYS_PER_PAGE);
                  const startIndex = (apiKeysPage - 1) * API_KEYS_PER_PAGE;
                  const displayedKeys = Math.min(API_KEYS_PER_PAGE, totalKeys - startIndex);
                  const hasPagination = totalPages > 1;
                  return `${displayedKeys * 72 + 44 + 72 + (hasPagination ? 52 : 0)}px`;
                })(),
                width: '1080px',
                top: '-40px',
                zIndex: 0,
              }}
            >
              {/* Titre API Keys + Summary + Filters + Search Bar */}
              <div className="px-6 pt-1 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D27',
                        lineHeight: '20px',
                      }}
                    >
                      API Keys
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        fontWeight: 500,
                      }}
                    >
                      {(() => {
                        const storageKeysCount = repo?.apiKeys?.length || 0;
                        const aiKeysCount = repo?.aiApiKeys?.length || 0;
                        const totalKeys = storageKeysCount + aiKeysCount;
                        
                        // Count by access type for storage keys
                        const readOnlyCount = repo?.apiKeys?.filter((k: any) => k.accessType === 'READ' || k.accessType === 'read').length || 0;
                        const readWriteCount = repo?.apiKeys?.filter((k: any) => k.accessType === 'READWRITE' || k.accessType === 'readwrite').length || 0;
                        
                        const parts = [];
                        parts.push(`${totalKeys} Total`);
                        parts.push(`${storageKeysCount} Storage`);
                        parts.push(`${aiKeysCount} AI`);
                        if (readOnlyCount > 0) parts.push(`${readOnlyCount} Read-Only`);
                        if (readWriteCount > 0) parts.push(`${readWriteCount} Read-Write`);
                        
                        return `(${parts.join(' • ')})`;
                      })()}
                    </span>
                    {/* Filter buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setApiKeyTypeFilter('all')}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid',
                          borderColor: apiKeyTypeFilter === 'all' ? '#617BFF' : '#E5E7EB',
                          backgroundColor: apiKeyTypeFilter === 'all' ? '#EEF2FF' : 'white',
                          color: apiKeyTypeFilter === 'all' ? '#617BFF' : '#6B7280',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setApiKeyTypeFilter('s3')}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid',
                          borderColor: apiKeyTypeFilter === 's3' ? '#617BFF' : '#E5E7EB',
                          backgroundColor: apiKeyTypeFilter === 's3' ? '#EEF2FF' : 'white',
                          color: apiKeyTypeFilter === 's3' ? '#617BFF' : '#6B7280',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        Storage Keys
                      </button>
                      <button
                        onClick={() => setApiKeyTypeFilter('ai')}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid',
                          borderColor: apiKeyTypeFilter === 'ai' ? '#617BFF' : '#E5E7EB',
                          backgroundColor: apiKeyTypeFilter === 'ai' ? '#EEF2FF' : 'white',
                          color: apiKeyTypeFilter === 'ai' ? '#617BFF' : '#6B7280',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        AI Keys
                      </button>
                    </div>
                  </div>
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={apiKeySearchQuery}
                    onChange={(e) => setApiKeySearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon
                          style={{
                            color: '#9CA3AF',
                            fontSize: '18px',
                            marginRight: '6px',
                          }}
                        />
                      ),
                    }}
                    sx={{
                      width: '240px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover fieldset': {
                          borderColor: '#D1D5DB',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#617BFF',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '6px 10px',
                        fontSize: '13px',
                        '&::placeholder': {
                          color: '#9CA3AF',
                          opacity: 1,
                        },
                      },
                    }}
                  />
              </div>
            </div>

            {/* Carte blanche principale */}
            <div
              className="bg-white rounded-xl border border-[#E9EAEB] relative"
              style={{
                height: (() => {
                  const totalKeys = (repo?.apiKeys?.length || 0) + (repo?.aiApiKeys?.length || 0);
                  if (totalKeys === 0) return `${244 + 72}px`;
                  const totalPages = Math.ceil(totalKeys / API_KEYS_PER_PAGE);
                  const startIndex = (apiKeysPage - 1) * API_KEYS_PER_PAGE;
                  const displayedKeys = Math.min(API_KEYS_PER_PAGE, totalKeys - startIndex);
                  const hasPagination = totalPages > 1;
                  return `${displayedKeys * 72 + 44 + 72 + (hasPagination ? 52 : 0)}px`;
                })(),
                width: '1080px',
                zIndex: 1,
              }}
            >
              {/* Contenu principal */}
              <div className="flex flex-col" style={{ height: '100%' }}>
                {/* Liste des API Keys (S3 + AI) */}
                <div className="px-4 pt-4" style={{ flex: 1 }}>
                  <div
                    className="overflow-x-auto"
                    style={{
                      width: '100%',
                      WebkitOverflowScrolling: 'touch',
                      ...hideScrollbarStyle,
                    }}
                  >
                    <table className="w-full" style={{ tableLayout: 'fixed', minWidth: '1000px' }}>
                      <thead>
                        <tr
                          className="border-b border-gray-200"
                          style={{
                            height: '44px',
                            boxSizing: 'border-box',
                          }}
                        >
                          <th
                            className="px-4 text-left"
                            style={{
                              height: '44px',
                              width: '200px',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '44px',
                            }}
                          >
                            Name
                          </th>
                          <th
                            className="px-4 text-left"
                            style={{
                              height: '44px',
                              width: '150px',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '44px',
                            }}
                          >
                            Endpoint
                          </th>
                          <th
                            className="px-4 text-left"
                            style={{
                              height: '44px',
                              width: '120px',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '44px',
                            }}
                          >
                            Mode
                          </th>
                          <th
                            className="px-4 text-left"
                            style={{
                              height: '44px',
                              width: '220px',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '44px',
                            }}
                          >
                            Key Value
                          </th>
                          <th
                            className="px-4 text-left"
                            style={{
                              height: '44px',
                              width: '150px',
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '44px',
                            }}
                          >
                            Created
                          </th>
                          <th
                            className="px-4 py-3 text-right"
                            style={{
                              fontWeight: 600,
                              fontSize: '12px',
                              color: '#717680',
                              lineHeight: '18px',
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" style={{ width: '100%' }}>
                        {((repo.apiKeys?.length || 0) + (repo.aiApiKeys?.length || 0)) === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-4">
                              <div
                                className="w-full flex items-center justify-center"
                                style={{ height: '200px' }}
                              >
                                <button
                                  onClick={(e) => setCreateApiKeyMenuAnchor(e.currentTarget)}
                                  style={{
                                    backgroundColor: '#617BFF',
                                    color: 'white',
                                    width: '200px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    borderWidth: '2px',
                                    borderColor: '#617BFF',
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  Get your first API key
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                {/* Menu for empty state button */}
                                <Menu
                                  anchorEl={createApiKeyMenuAnchor}
                                  open={Boolean(createApiKeyMenuAnchor)}
                                  onClose={() => setCreateApiKeyMenuAnchor(null)}
                                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                  PaperProps={{
                                    sx: {
                                      mt: 1,
                                      minWidth: '180px',
                                      borderRadius: '8px',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      setCreateApiKeyMenuAnchor(null);
                                      setNewS3ApiKey({ name: '', accessType: ApiTypes.AccessType.READ });
                                      setOpenCreateS3ApiKeyModal(true);
                                    }}
                                    sx={{ fontSize: '13px', padding: '10px 16px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <img src={cloudSvg} alt="S3" style={{ width: '20px', height: '20px' }} />
                                      <span>Storage API Key</span>
                                    </div>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      setCreateApiKeyMenuAnchor(null);
                                      setNewAIApiKey({ name: '', aiType: AiLlmTypes.AiType.OPENAI });
                                      setOpenCreateAIApiKeyModal(true);
                                    }}
                                    sx={{ fontSize: '13px', padding: '10px 16px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <AIIcon size={20} color="#617BFF" />
                                      <span>AI API Key</span>
                                    </div>
                                  </MenuItem>
                                </Menu>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          repo.apiKeys
                            ?.filter((apiKey) => {
                              // Type filter
                              if (apiKeyTypeFilter === 'ai') return false;
                              // Search filter
                              if (!apiKeySearchQuery) return true;
                              const searchLower = apiKeySearchQuery.toLowerCase();
                              return (
                                apiKey.name.toLowerCase().includes(searchLower) ||
                                apiKey.id.toLowerCase().includes(searchLower) ||
                                apiKey.key.toLowerCase().includes(searchLower)
                              );
                            })
                            .slice((apiKeysPage - 1) * API_KEYS_PER_PAGE, apiKeysPage * API_KEYS_PER_PAGE)
                            .map((apiKey) => (
                            <tr
                              key={apiKey.id}
                              className="hover:bg-gray-50 hover:border-l-2 hover:border-[#EBBDA3] transition-all duration-200 h-[72px]"
                            >
                              {/* Name Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                {editingApiKey?.id === apiKey.id ? (
                                  <TextField
                                    size="small"
                                    value={editingApiKey.name}
                                    onChange={(e) =>
                                      setEditingApiKey({
                                        ...editingApiKey,
                                        name: e.target.value,
                                      })
                                    }
                                    fullWidth
                                  />
                                ) : (
                                  <Tooltip title={apiKey.name}>
                                    <Typography
                                      variant="body2"
                                      className="truncate"
                                      style={{
                                        maxWidth: '150px',
                                        fontFamily: 'Geist, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        color: '#181D27',
                                      }}
                                    >
                                      {apiKey.name}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </td>
                              {/* Endpoint Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Chip
                                  label={repo.storageType || 'S3'}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#EEF2FF',
                                    color: '#617BFF',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                  }}
                                />
                              </td>
                              {/* Mode Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                {editingApiKey?.id === apiKey.id ? (
                                  <select
                                    value={editingApiKey.accessType}
                                    onChange={(e) =>
                                      setEditingApiKey({
                                        ...editingApiKey,
                                        accessType: e.target.value as ApiTypes.AccessType,
                                      })
                                    }
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                  >
                                    {Object.values(ApiTypes.AccessType).map((type) => (
                                      <option key={type} value={type}>
                                        {type === ApiTypes.AccessType.READ
                                          ? 'Read Only'
                                          : type === ApiTypes.AccessType.WRITE
                                          ? 'Write Only'
                                          : type}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    style={{
                                      fontFamily: 'Geist, sans-serif',
                                      fontWeight: 500,
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                      color: '#535862',
                                    }}
                                  >
                                    {apiKey.accessType}
                                  </Typography>
                                )}
                              </td>
                              {/* Key Value Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <code
                                    style={{
                                      fontFamily: 'monospace',
                                      fontSize: '13px',
                                      color: '#374151',
                                      backgroundColor: '#F9FAFB',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid #E5E7EB',
                                      maxWidth: '150px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {apiKey.key}
                                  </code>
                                  <Tooltip title="Copy API Key">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopy(apiKey.key)}
                                      className="text-[#788CFF] hover:text-[#788CFF]"
                                    >
                                      <ContentCopyIcon
                                        fontSize="small"
                                        style={{ color: '#788CFF' }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </td>
                              {/* Created Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontWeight: 400,
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    color: '#535862',
                                  }}
                                >
                                  {apiKey.createdAt
                                    ? new Date(apiKey.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })
                                    : '-'}
                                </Typography>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                {editingApiKey?.id === apiKey.id ? (
                                  <div className="flex justify-end space-x-2">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleUpdateApiKey(
                                          editingApiKey.id,
                                          editingApiKey.name,
                                          editingApiKey.accessType
                                        )
                                      }
                                      className="text-green-600 hover:text-green-800"
                                    >
                                      <span className="text-sm">Save</span>
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingApiKey(null)}
                                      className="text-gray-600 hover:text-gray-800"
                                    >
                                      <span className="text-sm">Cancel</span>
                                    </IconButton>
                                  </div>
                                ) : (
                                  <div className="flex justify-end space-x-2">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setEditingApiKey({
                                          id: apiKey.id,
                                          name: apiKey.name,
                                          accessType: apiKey.accessType,
                                          key: apiKey.key,
                                          secret: apiKey.secret?.key || '',
                                        })
                                      }
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteApiKey(apiKey.id)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        )
                        }
                        {/* AI API Keys - Merged in same table */}
                        {(() => {
                          // Calculate filtered S3 keys count to determine pagination offset for AI keys
                          const filteredS3KeysCount = apiKeyTypeFilter === 'ai' ? 0 : (repo.apiKeys?.filter((apiKey) => {
                            if (!apiKeySearchQuery) return true;
                            const searchLower = apiKeySearchQuery.toLowerCase();
                            return (
                              apiKey.name.toLowerCase().includes(searchLower) ||
                              apiKey.id.toLowerCase().includes(searchLower) ||
                              apiKey.key.toLowerCase().includes(searchLower)
                            );
                          }).length || 0);
                          
                          // Calculate pagination indices for AI keys
                          const pageStart = (apiKeysPage - 1) * API_KEYS_PER_PAGE;
                          const pageEnd = apiKeysPage * API_KEYS_PER_PAGE;
                          const aiStartIndex = Math.max(0, pageStart - filteredS3KeysCount);
                          const aiEndIndex = Math.max(0, pageEnd - filteredS3KeysCount);
                          
                          const filtered = repo.aiApiKeys?.filter((key: any) => {
                            // Type filter
                            if (apiKeyTypeFilter === 's3') return false;
                            // Search filter
                            if (!apiKeySearchQuery) return true;
                            const searchLower = apiKeySearchQuery.toLowerCase();
                            return (
                              (key.name || '').toLowerCase().includes(searchLower) ||
                              (key.aiType || '').toLowerCase().includes(searchLower) ||
                              (key.id || '').toLowerCase().includes(searchLower)
                            );
                          }) || [];
                          
                          return <>{filtered.slice(aiStartIndex, aiEndIndex).map((key: any) => {
                          const createdDate = key.createdAt ? new Date(key.createdAt) : null;
                          const formattedDate = createdDate 
                            ? createdDate.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })
                            : '-';
                          
                          return (
                            <tr
                              key={`ai-${key.id}`}
                              className="hover:bg-gray-50 hover:border-l-2 hover:border-[#EBBDA3] transition-all duration-200 h-[72px]"
                            >
                              {/* Name Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: '#181D27',
                                    lineHeight: '20px',
                                    maxWidth: '180px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {key.name || 'Unnamed Key'}
                                </Typography>
                              </td>

                              {/* Endpoint Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Chip
                                  label={key.aiType || 'OpenAI'}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#EEF2FF',
                                    color: '#617BFF',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                  }}
                                />
                              </td>

                              {/* Mode Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#9CA3AF',
                                  }}
                                >
                                  N/A
                                </Typography>
                              </td>

                              {/* Key Value Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <code
                                    style={{
                                      fontFamily: 'monospace',
                                      fontSize: '13px',
                                      color: '#374151',
                                      backgroundColor: '#F9FAFB',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid #E5E7EB',
                                      maxWidth: '150px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {key.key || '-'}
                                  </code>
                                  {key.key && (
                                    <Tooltip title="Copy API Key">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          navigator.clipboard.writeText(key.key);
                                          toastSuccess('API Key copied to clipboard');
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        <ContentCopyIcon sx={{ fontSize: 16, color: '#788CFF' }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </div>
                              </td>

                              {/* Created Column */}
                              <td className="px-4 py-4 whitespace-nowrap">
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontFamily: 'Geist, sans-serif',
                                    fontWeight: 400,
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    color: '#535862',
                                  }}
                                >
                                  {formattedDate}
                                </Typography>
                              </td>

                              {/* Action Column */}
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteAIApiKey(key.id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </td>
                            </tr>
                          );
                        })}</>;
                        })()}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {(() => {
                    // Calculate total filtered keys
                    const filteredS3Keys = apiKeyTypeFilter === 'ai' ? [] : (repo.apiKeys?.filter((apiKey) => {
                      if (!apiKeySearchQuery) return true;
                      const searchLower = apiKeySearchQuery.toLowerCase();
                      return (
                        apiKey.name.toLowerCase().includes(searchLower) ||
                        apiKey.id.toLowerCase().includes(searchLower) ||
                        apiKey.key.toLowerCase().includes(searchLower)
                      );
                    }) || []);
                    
                    const filteredAIKeys = apiKeyTypeFilter === 's3' ? [] : (repo.aiApiKeys?.filter((key: any) => {
                      if (!apiKeySearchQuery) return true;
                      const searchLower = apiKeySearchQuery.toLowerCase();
                      return (
                        (key.name || '').toLowerCase().includes(searchLower) ||
                        (key.aiType || '').toLowerCase().includes(searchLower) ||
                        (key.id || '').toLowerCase().includes(searchLower)
                      );
                    }) || []);
                    
                    const totalFilteredKeys = filteredS3Keys.length + filteredAIKeys.length;
                    const totalPages = Math.ceil(totalFilteredKeys / API_KEYS_PER_PAGE);
                    
                    if (totalPages <= 1) return null;
                    
                    // Generate page numbers to display
                    const getPageNumbers = () => {
                      const pages: (number | string)[] = [];
                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        if (apiKeysPage > 3) pages.push('...');
                        for (let i = Math.max(2, apiKeysPage - 1); i <= Math.min(totalPages - 1, apiKeysPage + 1); i++) {
                          pages.push(i);
                        }
                        if (apiKeysPage < totalPages - 2) pages.push('...');
                        pages.push(totalPages);
                      }
                      return pages;
                    };
                    
                    return (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          borderTop: '1px solid #F0F0F0',
                        }}
                      >
                        {/* Previous button */}
                        <button
                          onClick={() => setApiKeysPage(p => Math.max(1, p - 1))}
                          disabled={apiKeysPage === 1}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            backgroundColor: 'white',
                            color: apiKeysPage === 1 ? '#D1D5DB' : '#374151',
                            cursor: apiKeysPage === 1 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        
                        {/* Page numbers */}
                        {getPageNumbers().map((page, idx) => (
                          page === '...' ? (
                            <span key={`ellipsis-${idx}`} style={{ color: '#6B7280', fontSize: '13px' }}>...</span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => setApiKeysPage(page as number)}
                              style={{
                                minWidth: '32px',
                                height: '32px',
                                padding: '0 8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                                border: apiKeysPage === page ? '1px solid #617BFF' : '1px solid #E5E7EB',
                                backgroundColor: apiKeysPage === page ? '#EEF2FF' : 'white',
                                color: apiKeysPage === page ? '#617BFF' : '#374151',
                                fontSize: '13px',
                                fontWeight: apiKeysPage === page ? 600 : 400,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                              }}
                            >
                              {page}
                            </button>
                          )
                        ))}
                        
                        {/* Next button */}
                        <button
                          onClick={() => setApiKeysPage(p => Math.min(totalPages, p + 1))}
                          disabled={apiKeysPage === totalPages}
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB',
                            backgroundColor: 'white',
                            color: apiKeysPage === totalPages ? '#D1D5DB' : '#374151',
                            cursor: apiKeysPage === totalPages ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Bloc de 72px en bas avec les boutons Add API Key - Only show when there are keys */}
                {((repo.apiKeys?.length || 0) + (repo.aiApiKeys?.length || 0)) > 0 && (
                <div
                  className="flex justify-between items-center px-4"
                  style={{ height: '72px', borderTop: '1px solid #E9EAEB' }}
                >
                  {/* Add button on the LEFT - aligned with Buckets section above */}
                  <div className="flex gap-3">
                    <button
                      onClick={(e) => setCreateApiKeyMenuAnchor(e.currentTarget)}
                      style={{
                        backgroundColor: '#617BFF',
                        color: 'white',
                        width: '100px',
                        height: '36px',
                        borderRadius: '8px',
                        border: '2px solid #617BFF',
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      Add
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <Menu
                      anchorEl={createApiKeyMenuAnchor}
                      open={Boolean(createApiKeyMenuAnchor)}
                      onClose={() => setCreateApiKeyMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: '180px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setCreateApiKeyMenuAnchor(null);
                        setNewS3ApiKey({ name: '', accessType: ApiTypes.AccessType.READ });
                        setOpenCreateS3ApiKeyModal(true);
                      }}
                      sx={{
                        fontSize: '13px',
                        padding: '10px 16px',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img src={cloudSvg} alt="S3" style={{ width: '20px', height: '20px' }} />
                        <span>Storage API Key</span>
                      </div>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setCreateApiKeyMenuAnchor(null);
                        setNewAIApiKey({ name: '', aiType: AiLlmTypes.AiType.OPENAI });
                        setOpenCreateAIApiKeyModal(true);
                      }}
                      sx={{
                        fontSize: '13px',
                        padding: '10px 16px',
                        '&:hover': {
                          backgroundColor: '#F3F4F6',
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AIIcon size={20} color="#617BFF" />
                        <span>AI API Key</span>
                      </div>
                    </MenuItem>
                    </Menu>
                  </div>
                  {/* Count on the RIGHT - aligned with Buckets section above */}
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#717680',
                    }}
                  >
                    {((repo?.apiKeys?.length || 0) + (repo?.aiApiKeys?.length || 0)) > 0 && (
                      `${(repo?.apiKeys?.length || 0) + (repo?.aiApiKeys?.length || 0)} API key${((repo?.apiKeys?.length || 0) + (repo?.aiApiKeys?.length || 0)) !== 1 ? 's' : ''}`
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* END OF API Keys Section */}

      {/* Note: AI LLM Resources have been merged into the Resources section above */}
      {/* The separate AI LLM Resources section below is now hidden */}
      
      <div style={{ display: 'none' }}>
      {/* OLD AI LLM Resources Section - NOW MERGED IN RESOURCES ABOVE */}
      <div className="relative mt-0 mb-8 flex justify-center">
        {/* Carte grise en arrière-plan */}
        <div
          className="bg-gray-100 rounded-xl border border-[#E9EAEB] absolute"
          style={{
            height: `${40 + ((repo?.aiApiKeys?.length || 0) === 0 ? 244 + 72 : (repo?.aiApiKeys?.length || 0) * 72 + 44 + 72) + 2}px`,
            width: '1080px',
            top: '-40px',
            zIndex: 0,
          }}
        >
          {/* Titre API Keys (AI) */}
          <div className="px-6 pt-2">
            <span
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#181D27',
                lineHeight: '20px',
              }}
            >
              AI LLM Resources
            </span>
          </div>
        </div>

        {/* Carte blanche principale */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] relative"
          style={{
            height: `${(repo?.aiLlms?.length || 0) === 0 ? 316 : (repo?.aiLlms?.length || 0) * 72 + 44 + 72}px`,
            width: '1080px',
            zIndex: 1,
          }}
        >
          {/* Contenu principal */}
          <div className="flex flex-col" style={{ height: '100%' }}>
            {/* Liste des AI LLM Resources */}
            <div 
              className="overflow-y-auto px-4 pt-4" 
              style={{ 
                height: `${(repo?.aiLlms?.length || 0) === 0 ? 244 : (repo?.aiLlms?.length || 0) * 72 + 44}px`,
                ...hideScrollbarStyle
              }}
            >
              <div
                className="overflow-x-auto"
                style={{
                  width: '100%',
                  WebkitOverflowScrolling: 'touch',
                  ...hideScrollbarStyle,
                }}
              >
                <table className="w-full" style={{ tableLayout: 'fixed', minWidth: '1000px' }}>
                  <thead>
                    <tr
                      className="border-b border-gray-200"
                      style={{
                        height: '44px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '300px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Name
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '150px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Provider
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '250px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Base URL
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '180px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Created
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '100px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(repo?.aiLlms?.length || 0) === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <div
                            className="flex flex-col items-center justify-center"
                            style={{
                              height: '200px',
                              textAlign: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                              }}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="#9CA3AF">
                                <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#9CA3AF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#181D27',
                                lineHeight: '20px',
                                marginBottom: '4px',
                              }}
                            >
                              No AI LLM resources yet
                            </span>
                            <span
                              style={{
                                fontWeight: 400,
                                fontSize: '13px',
                                color: '#717680',
                                lineHeight: '18px',
                              }}
                            >
                              Add an AI LLM provider to get started with AI capabilities
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      repo?.aiLlms?.map((aiLlmAssoc: any) => {
                        const aiLlm = aiLlmAssoc.aiLlm || aiLlmAssoc;
                        const createdDate = aiLlm.createdAt ? new Date(aiLlm.createdAt) : null;
                        const formattedDate = createdDate 
                          ? createdDate.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })
                          : '-';
                        
                        return (
                          <tr
                            key={aiLlm.id || aiLlmAssoc.aiLlmId}
                            className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                            style={{
                              height: '72px',
                              boxSizing: 'border-box',
                            }}
                          >
                            {/* Name column */}
                            <td 
                              className="px-4" 
                              style={{ 
                                height: '72px',
                                verticalAlign: 'middle',
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span
                                  style={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: '#181D27',
                                    lineHeight: '20px',
                                  }}
                                >
                                  {aiLlm.name}
                                </span>
                              </div>
                            </td>

                            {/* Provider column */}
                            <td className="px-4">
                              <Chip
                                label={aiLlm.aiType || 'AI'}
                                size="small"
                                sx={{
                                  backgroundColor: aiLlm.aiType === 'OPENAI' ? '#10B981' : aiLlm.aiType === 'ANTHROPIC' ? '#617BFF' : aiLlm.aiType === 'AWS' ? '#FF9900' : aiLlm.aiType === 'GOOGLE' ? '#4285F4' : '#6B7280',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </td>

                            {/* Base URL column */}
                            <td 
                              className="px-4"
                              style={{
                                fontSize: '13px',
                                color: '#717680',
                              }}
                            >
                              {aiLlm.baseUrl || 'Default'}
                            </td>

                            {/* Created column */}
                            <td 
                              className="px-4"
                              style={{
                                fontSize: '13px',
                                color: '#717680',
                              }}
                            >
                              {formattedDate}
                            </td>

                            {/* Actions column */}
                            <td className="px-4">
                              <IconButton
                                size="small"
                                onClick={() => handleUnlinkAILLM(aiLlmAssoc.aiLlmId || aiLlm.id)}
                                sx={{ color: '#EF4444' }}
                              >
                                <DeleteIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex-shrink-0 border-t border-gray-200 px-6"
              style={{ height: '72px' }}
            >
              <div className="h-full flex items-center justify-between">
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#717680',
                  }}
                >
                  {(repo?.aiLlms?.length || 0) > 0 && (
                    `${repo?.aiLlms?.length} AI LLM resource${(repo?.aiLlms?.length || 0) !== 1 ? 's' : ''}`
                  )}
                </div>
                <button
                  onClick={() => {
                    loadAvailableAILLMs();
                    setOpenAddAILLMModal(true);
                  }}
                  style={{
                    backgroundColor: '#617BFF',
                    color: 'white',
                    width: '180px',
                    height: '36px',
                    borderRadius: '8px',
                    border: '2px solid #617BFF',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Add AI LLM Resource
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* END OF HIDDEN AI LLM Resources Section */}

      {/* Note: API Keys (AI) are now merged in the API Keys section above */}
      
      <div style={{ display: 'none' }}>
      {/* OLD AI API Keys Section - NOW MERGED IN API KEYS ABOVE */}
      <div className="relative mt-0 mb-8 flex justify-center">
        {/* Carte grise en arrière-plan */}
        <div
          className="bg-gray-100 rounded-xl border border-[#E9EAEB] absolute"
          style={{
            height: `${40 + ((repo?.aiApiKeys?.length || 0) === 0 ? 244 + 72 : (repo?.aiApiKeys?.length || 0) * 72 + 44 + 72) + 2}px`,
            width: '1080px',
            top: '-40px',
            zIndex: 0,
          }}
        >
          {/* Titre AI API Keys */}
          <div className="px-6 pt-2">
            <span
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#181D27',
                lineHeight: '20px',
              }}
            >
              API Keys
            </span>
          </div>
        </div>

        {/* Carte blanche principale */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] relative"
          style={{
            height: `${(repo?.aiApiKeys?.length || 0) === 0 ? 316 : (repo?.aiApiKeys?.length || 0) * 72 + 44 + 72}px`,
            width: '1080px',
            zIndex: 1,
          }}
        >
          {/* Contenu principal */}
          <div className="flex flex-col" style={{ height: '100%' }}>
            {/* Liste des AI API Keys */}
            <div 
              className="overflow-y-auto px-4 pt-4" 
              style={{ 
                height: `${(repo?.aiApiKeys?.length || 0) === 0 ? 244 : (repo?.aiApiKeys?.length || 0) * 72 + 44}px`,
                ...hideScrollbarStyle
              }}
            >
              <div
                className="overflow-x-auto"
                style={{
                  width: '100%',
                  WebkitOverflowScrolling: 'touch',
                  ...hideScrollbarStyle,
                }}
              >
                <table className="w-full" style={{ tableLayout: 'fixed', minWidth: '1000px' }}>
                  <thead>
                    <tr
                      className="border-b border-gray-200"
                      style={{
                        height: '44px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '200px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Name
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '150px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Endpoint
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '120px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Mode
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '220px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Key Value
                      </th>
                      <th
                        className="px-4 text-left"
                        style={{
                          height: '44px',
                          width: '150px',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '44px',
                        }}
                      >
                        Created
                      </th>
                      <th
                        className="px-4 py-3 text-right"
                        style={{
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#717680',
                          lineHeight: '18px',
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(repo?.aiApiKeys?.length || 0) === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div
                            className="flex flex-col items-center justify-center"
                            style={{
                              height: '200px',
                              textAlign: 'center',
                            }}
                          >
                            <div
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#F3F4F6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                              }}
                            >
                              <KeyIcon sx={{ fontSize: 24, color: '#9CA3AF' }} />
                            </div>
                            <span
                              style={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#181D27',
                                lineHeight: '20px',
                                marginBottom: '4px',
                              }}
                            >
                              No AI API keys yet
                            </span>
                            <span
                              style={{
                                fontWeight: 400,
                                fontSize: '13px',
                                color: '#717680',
                                lineHeight: '18px',
                              }}
                            >
                              AI API keys will appear here when created
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      repo?.aiApiKeys?.map((key: any) => {
                        const createdDate = key.createdAt ? new Date(key.createdAt) : null;
                        const formattedDate = createdDate 
                          ? createdDate.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })
                          : '-';
                        
                        return (
                          <tr
                            key={key.id}
                            className="border-b border-gray-200"
                            style={{
                              height: '72px',
                              boxSizing: 'border-box',
                            }}
                          >
                            {/* Name column */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '6px',
                                    backgroundColor: '#F9FAFB',
                                    border: '1px solid #E5E7EB',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <KeyIcon sx={{ fontSize: 16, color: '#667085' }} />
                                </div>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: '#181D27',
                                    lineHeight: '20px',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {key.name || 'Unnamed Key'}
                                </Typography>
                              </div>
                            </td>

                            {/* Endpoint column */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <Chip
                                label={key.aiType || 'OpenAI'}
                                size="small"
                                sx={{
                                  backgroundColor: key.aiType === 'OPENAI' ? '#10B981' : key.aiType === 'ANTHROPIC' ? '#617BFF' : '#F0F9FF',
                                  color: key.aiType === 'OPENAI' || key.aiType === 'ANTHROPIC' ? 'white' : '#0369A1',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </td>

                            {/* Mode column */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <Typography
                                variant="body2"
                                style={{
                                  fontFamily: 'Geist, sans-serif',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: '#9CA3AF',
                                }}
                              >
                                N/A
                              </Typography>
                            </td>

                            {/* Key Value column */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <code
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    color: '#374151',
                                    backgroundColor: '#F9FAFB',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #E5E7EB',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {key.key || '-'}
                                </code>
                                {key.key && (
                                  <Tooltip title="Copy API Key">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(key.key);
                                        toastSuccess('API Key copied to clipboard');
                                      }}
                                      sx={{ p: 0.5 }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: 16, color: '#667085' }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </div>
                            </td>

                            {/* Created column */}
                            <td className="px-4 py-4 whitespace-nowrap">
                              <Typography
                                variant="body2"
                                style={{
                                  fontFamily: 'Geist, sans-serif',
                                  fontWeight: 400,
                                  fontSize: '13px',
                                  lineHeight: '18px',
                                  color: '#535862',
                                }}
                              >
                                {formattedDate}
                              </Typography>
                            </td>

                            {/* Action column */}
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAIApiKey(key.id)}
                                sx={{ color: '#EF4444' }}
                              >
                                <DeleteIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div
              className="flex-shrink-0 border-t border-gray-200 px-6"
              style={{ height: '72px' }}
            >
              <div className="h-full flex items-center justify-between">
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#717680',
                  }}
                >
                  {(repo?.aiApiKeys?.length || 0) > 0 && (
                    `${repo?.aiApiKeys?.length} AI API key${(repo?.aiApiKeys?.length || 0) !== 1 ? 's' : ''}`
                  )}
                </div>
                <button
                  onClick={() => setOpenCreateAIApiKeyModal(true)}
                  style={{
                    backgroundColor: '#10B981',
                    color: 'white',
                    width: '140px',
                    height: '36px',
                    borderRadius: '8px',
                    border: '2px solid #10B981',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  Add AI API Key
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* END OF HIDDEN AI API Keys Section */}

      {/* Espacement avant Explore our guides */}
      <div style={{ height: '80px' }}></div>

      {/* Explore our guides Section */}
      <div ref={guidesRef} className="relative mt-0 mb-8 flex justify-center">
        {/* Carte grise en arrière-plan */}
        <div
          className="bg-gray-100 rounded-xl border border-[#E9EAEB] absolute"
          style={{ height: '317px', width: '1080px', top: '-40px', zIndex: 0 }}
        >
          {/* Titre Explore our guides */}
          <div className="px-6 pt-2">
            <span
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#181D27',
                lineHeight: '20px',
              }}
            >
              Explore our guides
            </span>
          </div>
        </div>

        {/* Carte blanche principale */}
        <div
          className="bg-white rounded-xl border border-[#E9EAEB] relative"
          style={{ height: '275px', width: '1080px', zIndex: 1 }}
        >
          {/* Contenu des guides */}
          <div className="flex flex-col h-full p-4">
            <div
              style={{
                maxHeight: 240,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <div className="flex space-x-4" style={{ paddingBottom: '4px' }}>
                {/* Guide 1: Create a Repository */}
                <div
                  style={{
                    width: 190,
                    height: 235,
                    minWidth: '190px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                      Create a Repository
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                      Learn how to create a new storage repository
                    </p>
                  </div>
                  <div
                    onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage-1/create-a-repository', '_blank')}
                    style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    View guide <span style={{ fontSize: '14px' }}>→</span>
                  </div>
                </div>

                {/* Guide 2: Configure a Repository */}
                <div
                  style={{
                    width: 190,
                    height: 235,
                    minWidth: '190px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                      Configure a Repository
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                      Set up and configure your repository settings
                    </p>
                  </div>
                  <div
                    onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage-1/program-with-flashback', '_blank')}
                    style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    View guide <span style={{ fontSize: '14px' }}>→</span>
                  </div>
                </div>

                {/* Guide 3: Store an Object */}
                <div
                  style={{
                    width: 190,
                    height: 235,
                    minWidth: '190px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                      Store an Object
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                      Upload and store objects in your repository
                    </p>
                  </div>
                  <div
                    onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/test-a-repository/program-with-flashback', '_blank')}
                    style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    View guide <span style={{ fontSize: '14px' }}>→</span>
                  </div>
                </div>

                {/* Guide 4: Send a Prompt */}
                <div
                  style={{
                    width: 190,
                    height: 235,
                    minWidth: '190px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                      Send a Prompt
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                      Send AI prompts through your repository
                    </p>
                  </div>
                  <div
                    onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/test-a-repository/program-with-flashback-1', '_blank')}
                    style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    View guide <span style={{ fontSize: '14px' }}>→</span>
                  </div>
                </div>

                {/* Guide 5: Repository API Reference */}
                <div
                  style={{
                    width: 190,
                    height: 235,
                    minWidth: '190px',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: 'white',
                  }}
                >
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                      Repository API Reference
                    </h3>
                    <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                      Explore the complete Repository API documentation
                    </p>
                  </div>
                  <div
                    onClick={() => window.open('https://docs.flashback.tech/support-reference/platform-api-reference/repo-management', '_blank')}
                    style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    View docs <span style={{ fontSize: '14px' }}>→</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Affichage conditionnel du formulaire d'ajout de bucket */}
      {openEditUnit && (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="text-black" style={{ color: '#788CFF' }}>
              Create Storage Unit
            </Typography>
            <IconButton onClick={() => setOpenEditUnit(false)}>
              <ArrowBackIcon />
            </IconButton>
          </div>

          <div className="space-y-4">
            <CommonFields
              name={newUnit.name || ''}
              storageType={newUnit.storageType || ApiTypes.StorageType.S3}
              validationErrors={validationErrors}
              onChange={(field, value) => {
                setNewUnit((prev) => ({ ...prev, [field]: value }));
                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[field];
                  return newErrors;
                });
              }}
            />

            <StorageTypeFields
              storageType={newUnit.storageType || ApiTypes.StorageType.S3}
              s3Fields={s3Fields}
              gcsFields={gcsFields}
              azureFields={azureFields}
              validationErrors={validationErrors}
              isEdit={false}
              onChange={(field, value) => {
                switch (newUnit.storageType) {
                  case ApiTypes.StorageType.S3:
                    setS3Fields((prev) => ({ ...prev, [field]: value }));
                    break;
                  case ApiTypes.StorageType.GCS:
                    setGcsFields((prev) => ({ ...prev, [field]: value }));
                    break;
                  case ApiTypes.StorageType.BLOB:
                    setAzureFields((prev) => ({ ...prev, [field]: value }));
                    break;
                }

                setValidationErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors[field];
                  return newErrors;
                });
              }}
            />
          </div>

          <div className="mt-6 flex justify-end">
            <StyledButton
              text="Create Bucket"
              onClick={handleCreateUnit}
              icon={StyledButtonIcon.Plus}
            />
          </div>
        </div>
      )}

      {/* Add Bucket to Repo Dialog */}
      <Transition appear show={openAddBucket} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-[100]"
          onClose={() => {
            setOpenAddBucket(false);
            setSelectedBucketToAdd(null);
            setBucketFolder('');
            setShowCreateNewBucket(false);
          }}
        >
          {/* Overlay */}
          <div className="fixed inset-0 bg-black bg-opacity-25 blur-background z-50" />

          {/* Dialog Content */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <div className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all border-l-4 border-[#617BFF] no-blur relative">
                {/* Close button */}
                <button
                  onClick={() => {
                    setOpenAddBucket(false);
                    setSelectedBucketToAdd(null);
                    setBucketFolder('');
                    setShowCreateNewBucket(false);
                  }}
                  className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  title="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <HeadlessUIDialog.Title
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900 mb-2 pr-8"
                >
                  {showCreateNewBucket ? 'Create New Resource' : 'Add Resource to Repository'}
                </HeadlessUIDialog.Title>

                <p className="text-sm text-gray-600 mb-6">
                  {showCreateNewBucket
                    ? 'Configure a new bucket with your cloud storage credentials.'
                    : 'Link an existing bucket or create a new one.'}
                </p>

                {!showCreateNewBucket ? (
                  /* Select existing bucket */
                  <div className="space-y-5">
                    <button
                      onClick={() => {
                        setOpenAddBucket(false);
                        // Set trigger to open add bucket form in StorageUnits (expects '1')
                        localStorage.setItem('triggerAddBucket', '1');
                        // Dispatch navigation event (same pattern as OverviewMockup)
                        try {
                          const rootEl = document.getElementById('root');
                          if (rootEl) {
                            rootEl.dispatchEvent(
                              new CustomEvent('navigateToBuckets', {
                                detail: { tabIndex: TabIndex.StorageUnits },
                              })
                            );
                          }
                          window.dispatchEvent(
                            new CustomEvent('navigateToBuckets', {
                              detail: { tabIndex: TabIndex.StorageUnits },
                            })
                          );
                        } catch (e) { /* ignore */ }
                        navigate('/cloud-storage/buckets');
                        // Dispatch again after navigation
                        setTimeout(() => {
                          try {
                            const rootEl2 = document.getElementById('root');
                            if (rootEl2) {
                              rootEl2.dispatchEvent(
                                new CustomEvent('navigateToBuckets', {
                                  detail: { tabIndex: TabIndex.StorageUnits },
                                })
                              );
                            }
                            window.dispatchEvent(
                              new CustomEvent('navigateToBuckets', {
                                detail: { tabIndex: TabIndex.StorageUnits },
                              })
                            );
                          } catch (e) { /* ignore */ }
                        }, 0);
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Create New Bucket
                    </button>

                    <div className="flex items-center justify-center py-2">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="px-3 text-sm text-gray-500">or</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Bucket
                      </label>
                      <Autocomplete
                        options={availableUnits.filter(
                          (unit) => !repo?.buckets.some((b: any) => b.bucketId === unit.id)
                        )}
                        getOptionLabel={(option) => `${option.name} (${option.bucket})`}
                        value={selectedBucketToAdd}
                        onChange={(_, newValue) => setSelectedBucketToAdd(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select a bucket..."
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#e5e7eb' },
                                '&:hover fieldset': { borderColor: '#617BFF' },
                                '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                              },
                            }}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="No available buckets"
                      />
                    </div>

                    <div className="mt-8 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={() => {
                          setOpenAddBucket(false);
                          setSelectedBucketToAdd(null);
                          setBucketFolder('');
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                        style={{ backgroundColor: '#617BFF' }}
                        onClick={handleAddBucketToRepo}
                        disabled={!selectedBucketToAdd}
                      >
                        Add Bucket
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Create new bucket form */
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bucket Name
                      </label>
                      <input
                        type="text"
                        value={newUnit.name}
                        onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                        placeholder="My Bucket"
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Storage Type
                      </label>
                      <select
                        value={newUnit.storageType}
                        onChange={(e) => setNewUnit({ ...newUnit, storageType: e.target.value as ApiTypes.StorageType })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all cursor-pointer"
                        style={{ fontSize: '14px' }}
                      >
                        <option value={ApiTypes.StorageType.S3}>Amazon S3</option>
                        <option value={ApiTypes.StorageType.GCS}>Google Cloud Storage</option>
                        <option value={ApiTypes.StorageType.BLOB}>Azure Blob Storage</option>
                      </select>
                    </div>

                    {/* S3 Fields */}
                    {newUnit.storageType === ApiTypes.StorageType.S3 && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">S3 Bucket Name</label>
                          <input
                            type="text"
                            value={s3Fields.bucket}
                            onChange={(e) => setS3Fields({ ...s3Fields, bucket: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="my-s3-bucket"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Access Key</label>
                            <input
                              type="text"
                              value={s3Fields.accessKey}
                              onChange={(e) => setS3Fields({ ...s3Fields, accessKey: e.target.value })}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                              placeholder="AKIA..."
                              style={{ fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Secret Key</label>
                            <input
                              type="password"
                              value={s3Fields.secret}
                              onChange={(e) => setS3Fields({ ...s3Fields, secret: e.target.value })}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                              placeholder="••••••••"
                              style={{ fontSize: '14px' }}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Region <span className="text-xs text-gray-500">(Optional)</span></label>
                            <input
                              type="text"
                              value={s3Fields.region}
                              onChange={(e) => setS3Fields({ ...s3Fields, region: e.target.value })}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                              placeholder="us-east-1"
                              style={{ fontSize: '14px' }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Endpoint <span className="text-xs text-gray-500">(Optional)</span></label>
                            <input
                              type="text"
                              value={s3Fields.endpoint}
                              onChange={(e) => setS3Fields({ ...s3Fields, endpoint: e.target.value })}
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                              placeholder="https://s3.amazonaws.com"
                              style={{ fontSize: '14px' }}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* GCS Fields */}
                    {newUnit.storageType === ApiTypes.StorageType.GCS && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">GCS Bucket Name</label>
                          <input
                            type="text"
                            value={gcsFields.bucket}
                            onChange={(e) => setGcsFields({ ...gcsFields, bucket: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="my-gcs-bucket"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Client Email</label>
                          <input
                            type="email"
                            value={gcsFields.clientEmail}
                            onChange={(e) => setGcsFields({ ...gcsFields, clientEmail: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="service-account@project.iam.gserviceaccount.com"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Private Key</label>
                          <textarea
                            value={gcsFields.privateKey}
                            onChange={(e) => setGcsFields({ ...gcsFields, privateKey: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="-----BEGIN PRIVATE KEY-----..."
                            rows={3}
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                      </>
                    )}

                    {/* Azure Fields */}
                    {newUnit.storageType === ApiTypes.StorageType.BLOB && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Storage Account</label>
                          <input
                            type="text"
                            value={azureFields.storageAccount}
                            onChange={(e) => setAzureFields({ ...azureFields, storageAccount: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="mystorageaccount"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Container Name</label>
                          <input
                            type="text"
                            value={azureFields.container}
                            onChange={(e) => setAzureFields({ ...azureFields, container: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="my-container"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Access Key</label>
                          <input
                            type="password"
                            value={azureFields.accessKey}
                            onChange={(e) => setAzureFields({ ...azureFields, accessKey: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent"
                            placeholder="••••••••"
                            style={{ fontSize: '14px' }}
                          />
                        </div>
                      </>
                    )}

                    <div className="mt-8 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={() => setShowCreateNewBucket(false)}
                        disabled={creatingBucket}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                        style={{ backgroundColor: '#617BFF' }}
                        onClick={handleCreateAndLinkBucket}
                        disabled={!newUnit.name || creatingBucket}
                      >
                        {creatingBucket ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            Creating...
                          </span>
                        ) : (
                          'Create & Add Bucket'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Modal Create AI API Key */}
      <Transition appear show={openCreateAIApiKeyModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => setOpenCreateAIApiKeyModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#EEF2FF' }}>
                      <AIIcon size={24} color="#617BFF" />
                    </div>
                    <div>
                      <HeadlessUIDialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Create Repo AI API Key
                      </HeadlessUIDialog.Title>
                      <p className="text-sm text-gray-500 mt-1">
                        Generate a new API key to access AI LLM capabilities
                      </p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Key Name
                      </label>
                      <input
                        type="text"
                        value={newAIApiKey.name}
                        onChange={(e) => setNewAIApiKey({ ...newAIApiKey, name: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                        placeholder="My AI API Key"
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI SDK Provider
                      </label>
                      <select
                        value={newAIApiKey.aiType}
                        onChange={(e) => setNewAIApiKey({ ...newAIApiKey, aiType: e.target.value as AiLlmTypes.AiType })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all cursor-pointer"
                        style={{ fontSize: '14px' }}
                      >
                        <option value={AiLlmTypes.AiType.OPENAI}>OpenAI SDK</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setOpenCreateAIApiKeyModal(false)}
                      disabled={creatingAIApiKey}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                      style={{ backgroundColor: '#617BFF' }}
                      onClick={handleCreateAIApiKey}
                      disabled={creatingAIApiKey}
                    >
                      {creatingAIApiKey ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        'Create'
                      )}
                    </button>
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Modal Create Storage API Key */}
      <Transition appear show={openCreateS3ApiKeyModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => setOpenCreateS3ApiKeyModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#EEF2FF' }}>
                      <img src={cloudSvg} alt="S3" style={{ width: '28px', height: '28px' }} />
                    </div>
                    <div>
                      <HeadlessUIDialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Create Repo Storage API Key
                      </HeadlessUIDialog.Title>
                      <p className="text-sm text-gray-500 mt-1">
                        Generate a new API key for storage access (S3, Azure Blob Storage, etc.)
                      </p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Key Name
                      </label>
                      <input
                        type="text"
                        value={newS3ApiKey.name}
                        onChange={(e) => setNewS3ApiKey({ ...newS3ApiKey, name: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                        placeholder="My Storage API Key"
                        style={{ fontSize: '14px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Access Mode
                      </label>
                      <select
                        value={newS3ApiKey.accessType}
                        onChange={(e) => setNewS3ApiKey({ ...newS3ApiKey, accessType: e.target.value as ApiTypes.AccessType })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all cursor-pointer"
                        style={{ fontSize: '14px' }}
                      >
                        {Object.values(ApiTypes.AccessType).map((type) => (
                          <option key={type} value={type}>
                            {type === ApiTypes.AccessType.READ
                              ? 'Read Only'
                              : type === ApiTypes.AccessType.WRITE
                              ? 'Write Only'
                              : type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setOpenCreateS3ApiKeyModal(false)}
                      disabled={creatingS3ApiKey}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                      style={{ backgroundColor: '#617BFF' }}
                      onClick={handleCreateS3ApiKey}
                      disabled={creatingS3ApiKey}
                    >
                      {creatingS3ApiKey ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                          Creating...
                        </span>
                      ) : (
                        'Create'
                      )}
                    </button>
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Modal S3 Secret (One-Time Display) */}
      <Transition appear show={openS3SecretModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setOpenS3SecretModal(false);
            setCreatedS3Key(null);
            setS3KeyCopied(false);
            setS3SecretCopied(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <HeadlessUIDialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Storage API Key Created
                      </HeadlessUIDialog.Title>
                      <p className="text-sm text-gray-500 mt-1">
                        Save these credentials now. The secret will not be shown again.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-sm text-amber-800">
                          <strong>Important:</strong> Copy and save these credentials securely. The secret key will only be shown once.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                        Public Key
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800 overflow-x-auto">
                          {createdS3Key?.key || ''}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(createdS3Key?.key || '');
                            setS3KeyCopied(true);
                            setTimeout(() => setS3KeyCopied(false), 2000);
                          }}
                          className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            s3KeyCopied 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {s3KeyCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                        Secret
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800 overflow-x-auto">
                          {createdS3Key?.secret || ''}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(createdS3Key?.secret || '');
                            setS3SecretCopied(true);
                            setTimeout(() => setS3SecretCopied(false), 2000);
                          }}
                          className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            s3SecretCopied 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {s3SecretCopied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all hover:shadow-md"
                      style={{ backgroundColor: '#617BFF' }}
                      onClick={() => setShowS3ConfirmClose(true)}
                    >
                      Done
                    </button>
                  </div>

                  {/* Confirmation popup */}
                  {showS3ConfirmClose && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900">
                            Have you saved your credentials?
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-6">
                          Make sure you have copied and saved your API key and secret. They will not be shown again.
                        </p>
                        <div className="flex gap-3 justify-end">
                          <button
                            onClick={() => setShowS3ConfirmClose(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            Go back
                          </button>
                          <button
                            onClick={() => {
                              setShowS3ConfirmClose(false);
                              setOpenS3SecretModal(false);
                              setCreatedS3Key(null);
                              setS3KeyCopied(false);
                              setS3SecretCopied(false);
                              toastSuccess('Storage API Key created successfully');
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#617BFF' }}
                          >
                            Yes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Modal AI Secret (One-Time Display) */}
      <Transition appear show={openAISecretModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setOpenAISecretModal(false);
            setCreatedAIKey(null);
            setAIKeyCopied(false);
            setAISecretCopied(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all border-l-4 border-[#10B981]">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <HeadlessUIDialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        AI API Key Created Successfully!
                      </HeadlessUIDialog.Title>
                      <p className="text-sm text-gray-600 mt-1">
                        Your new API key is ready to use
                      </p>
                    </div>
                  </div>

                  <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex gap-2">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-red-800">
                          Important: Save these credentials now
                        </p>
                        <p className="text-xs text-red-700 mt-0.5">
                          The secret will only be shown once and cannot be retrieved later.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Secret */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Secret <span className="text-red-600 text-xs">(shown only once)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-4 py-3 bg-red-50 border border-red-300 rounded-lg text-sm font-mono break-all" style={{ fontSize: '13px' }}>
                          {createdAIKey?.secret}
                        </code>
                        <button
                          onClick={() => copyAIKeyToClipboard(createdAIKey?.secret || '', 'secret')}
                          className="flex-shrink-0 px-4 py-3 bg-red-100 border border-red-300 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors"
                        >
                          {aiSecretCopied ? (
                            <span className="flex items-center gap-1.5 text-red-700">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Copied
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-red-700">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all hover:shadow-md"
                      style={{ backgroundColor: '#10B981' }}
                      onClick={() => setShowAIConfirmClose(true)}
                    >
                      Done
                    </button>
                  </div>

                  {/* Confirmation popup */}
                  {showAIConfirmClose && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                      <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900">
                            Have you saved your credentials?
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-6">
                          Make sure you have copied and saved your API key and secret. They will not be shown again.
                        </p>
                        <div className="flex gap-3 justify-end">
                          <button
                            onClick={() => setShowAIConfirmClose(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            Go back
                          </button>
                          <button
                            onClick={() => {
                              setShowAIConfirmClose(false);
                              setOpenAISecretModal(false);
                              setCreatedAIKey(null);
                              setAIKeyCopied(false);
                              setAISecretCopied(false);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#617BFF' }}
                          >
                            Yes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Modal Add AI LLM Resource */}
      <Transition appear show={openAddAILLMModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setOpenAddAILLMModal(false);
            setShowCreateNewAILLM(false);
            setSelectedAILLM(null);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all border-l-4 border-[#617BFF]">
                  <HeadlessUIDialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900 mb-2"
                  >
                    {showCreateNewAILLM ? 'Create New AI LLM Resource' : 'Add AI LLM Resource'}
                  </HeadlessUIDialog.Title>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    {showCreateNewAILLM
                      ? 'Configure a new AI LLM provider with your API credentials.'
                      : 'Link an existing AI LLM resource or create a new one.'}
                  </p>

                  {!showCreateNewAILLM ? (
                    /* Link existing AI LLM */
                    <div className="space-y-5">
                      <button
                        onClick={() => {
                          setOpenAddAILLMModal(false);
                          // Set trigger to open add AI LLM form
                          localStorage.setItem('triggerAddAILLM', '1');
                          // Dispatch navigation event (same pattern as buckets)
                          try {
                            const rootEl = document.getElementById('root');
                            if (rootEl) {
                              rootEl.dispatchEvent(
                                new CustomEvent('navigateToTab', {
                                  detail: { tabIndex: TabIndex.AILLM },
                                })
                              );
                            }
                            window.dispatchEvent(
                              new CustomEvent('navigateToTab', {
                                detail: { tabIndex: TabIndex.AILLM },
                              })
                            );
                          } catch (e) { /* ignore */ }
                          navigate('/ai/llm');
                          // Dispatch again after navigation
                          setTimeout(() => {
                            try {
                              const rootEl2 = document.getElementById('root');
                              if (rootEl2) {
                                rootEl2.dispatchEvent(
                                  new CustomEvent('navigateToTab', {
                                    detail: { tabIndex: TabIndex.AILLM },
                                  })
                                );
                              }
                              window.dispatchEvent(
                                new CustomEvent('navigateToTab', {
                                  detail: { tabIndex: TabIndex.AILLM },
                                })
                              );
                            } catch (e) { /* ignore */ }
                          }, 0);
                        }}
                        className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Create New AI LLM Resource
                      </button>

                      <div className="flex items-center justify-center py-2">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="px-3 text-sm text-gray-500">or</span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select AI LLM Resource
                        </label>
                        <Autocomplete
                          options={availableAILLMs}
                          getOptionLabel={(option) => `${option.name} (${option.aiType})`}
                          value={selectedAILLM}
                          onChange={(_, newValue) => setSelectedAILLM(newValue)}
                          loading={loadingAILLMs}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select an AI LLM..."
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: '#e5e7eb' },
                                  '&:hover fieldset': { borderColor: '#617BFF' },
                                  '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                                },
                              }}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          noOptionsText="No AI LLMs available"
                        />
                      </div>

                      <div className="mt-8 flex justify-end space-x-3">
                        <button
                          type="button"
                          className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            setOpenAddAILLMModal(false);
                            setSelectedAILLM(null);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                          style={{ backgroundColor: '#617BFF' }}
                          onClick={handleLinkExistingAILLM}
                          disabled={!selectedAILLM || linkingAILLM}
                        >
                          {linkingAILLM ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Linking...
                            </span>
                          ) : (
                            'Link Resource'
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Create new AI LLM */
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Resource Name
                        </label>
                        <input
                          type="text"
                          value={newAILLM.name}
                          onChange={(e) => setNewAILLM({ ...newAILLM, name: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                          placeholder="My OpenAI Account"
                          style={{ fontSize: '14px' }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Provider
                        </label>
                        <select
                          value={newAILLM.aiType}
                          onChange={(e) => setNewAILLM({ ...newAILLM, aiType: e.target.value as AiLlmTypes.AiType })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all cursor-pointer"
                          style={{ fontSize: '14px' }}
                        >
                          <option value={AiLlmTypes.AiType.OPENAI}>OpenAI (GPT-4, GPT-3.5)</option>
                          <option value={AiLlmTypes.AiType.ANTHROPIC}>Anthropic (Claude)</option>
                          <option value={AiLlmTypes.AiType.GOOGLE}>Google (Gemini)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          API Key
                        </label>
                        <input
                          type="password"
                          value={newAILLM.key || ''}
                          onChange={(e) => setNewAILLM({ ...newAILLM, key: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                          placeholder="sk-..."
                          style={{ fontSize: '14px' }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Base URL <span className="text-xs text-gray-500">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          value={newAILLM.endpoint || ''}
                          onChange={(e) => setNewAILLM({ ...newAILLM, endpoint: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#617BFF] focus:border-transparent transition-all"
                          placeholder="https://api.openai.com/v1"
                          style={{ fontSize: '14px' }}
                        />
                      </div>

                      <div className="mt-8 flex justify-end space-x-3">
                        <button
                          type="button"
                          className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          onClick={() => setShowCreateNewAILLM(false)}
                          disabled={creatingAILLM}
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          className="px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                          style={{ backgroundColor: '#617BFF' }}
                          onClick={handleCreateAndLinkAILLM}
                          disabled={creatingAILLM}
                        >
                          {creatingAILLM ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Creating...
                            </span>
                          ) : (
                            'Create & Link'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Auto-Attach Modal */}
      <Transition appear show={showAutoAttachModal} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-50"
          onClose={() => setShowAutoAttachModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-center mb-4">
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: '#EEF2FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  
                  <HeadlessUIDialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900 text-center mb-2"
                  >
                    Resource Created Successfully!
                  </HeadlessUIDialog.Title>
                  
                  <p className="text-sm text-gray-600 text-center mb-6">
                    Would you like to attach <span className="font-semibold text-gray-900">{pendingAttachment?.resourceName}</span> to repository <span className="font-semibold text-gray-900">{pendingAttachment?.repoName}</span>?
                  </p>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowAutoAttachModal(false);
                        setPendingAttachment(null);
                        toastSuccess('Resource created successfully');
                      }}
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all hover:shadow-md"
                      style={{ backgroundColor: '#617BFF' }}
                      onClick={async () => {
                        if (!pendingAttachment?.resourceId) return;
                        
                        try {
                          // Here we would call the API to attach the resource
                          // For now, just refresh the repo data
                          toastSuccess(`Resource attached to ${pendingAttachment.repoName} successfully!`);
                          setShowAutoAttachModal(false);
                          setPendingAttachment(null);
                          
                          // Reload repo data
                          await reloadRepoData();
                        } catch (error) {
                          toastError('Failed to attach resource');
                        }
                      }}
                    >
                      Attach to Repository
                    </button>
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>
    </div>
  );
}

export default StorageRepoDetails;
